import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Card, Table, Button, Space, Tag, Tabs, message, Segmented, Modal, Form, Row, Col, Select, Input, Typography, Badge, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import type { TabsProps } from 'antd';
import request from '../../../utils/request';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../hooks/useAuth';
import { usePermissions } from '../../../contexts/PermissionContext';
import TimezoneAwareTime from '../../../components/TimezoneAwareTime';
import { getInventoryUnitDetail } from '../../../services/inventory';
import { getCurrentUserProjects } from '../../../services/project';
import { getAllWarehouses } from '../../../services/warehouse';

const StaffExecutionTodoPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { permissions, isWarehouseStaff } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [pager, setPager] = useState<{ current: number; pageSize: number; total: number }>({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [mode, setMode] = useState<'instruction'|'internal'|'batch'>('batch'); // 类别：客户指令/仓内工单/批次执行
  const [targetsOf, setTargetsOf] = useState<any | null>(null); // 预估操作数量弹窗
  const [historyTargetsOf, setHistoryTargetsOf] = useState<any | null>(null);
  const [historyTargetsRows, setHistoryTargetsRows] = useState<any[]>([]);
  const [historyTargetsLoading, setHistoryTargetsLoading] = useState(false);
  const [historyTargetsPage, setHistoryTargetsPage] = useState(1);
  const [historyTargetsPageSize, setHistoryTargetsPageSize] = useState(10);
  const [historyInputIocRowsCacheByInstructionId, setHistoryInputIocRowsCacheByInstructionId] = useState<Record<string, any[]>>({});
  const [tab, setTab] = useState<'todo'|'mine'|'done'>('todo');
  const [countByModeTab, setCountByModeTab] = useState<Record<string, number>>({});
  const [form] = Form.useForm();
  const formValuesWatch = Form.useWatch([], form);
  const [pageStateReady, setPageStateReady] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplOptions, setTplOptions] = useState<Array<{ value: string; label: React.ReactNode; code?: string; name?: string; level?: string }>>([]);
  const [inventoryModalOpen, setInventoryModalOpen] = useState(false);
  const [inventoryModalRows, setInventoryModalRows] = useState<any[]>([]);
  const [inventoryModalTitle, setInventoryModalTitle] = useState<string>('');
  const assignedWarehouseIds = useMemo(
    () => ((permissions?.assigned_warehouse_ids || []) as number[]).map((x) => Number(x)).filter((x) => Number.isFinite(x)),
    [permissions?.assigned_warehouse_ids],
  );
  const isWarehouseScopedUser = useMemo(() => isWarehouseStaff(), [isWarehouseStaff]);
  const warehouseOptionsForExecution = useMemo(() => {
    if (!isWarehouseScopedUser) return warehouses;
    if (assignedWarehouseIds.length === 0) return [];
    const allowed = new Set(assignedWarehouseIds.map((id) => String(id)));
    return (warehouses || []).filter((w: any) => allowed.has(String(w?.id)));
  }, [warehouses, isWarehouseScopedUser, assignedWarehouseIds]);

  useEffect(() => {
    if (!isWarehouseScopedUser) return;
    if (warehouseOptionsForExecution.length === 0) return;
    const allowed = new Set(warehouseOptionsForExecution.map((w: any) => String(w?.id)));
    const currentInstructionWh = String(form.getFieldValue('warehouse_id') || '');
    const currentBatchWh = String(form.getFieldValue('batch_warehouse_id') || '');
    const fallbackWh = String(warehouseOptionsForExecution[0]?.id || '');
    const next: Record<string, string> = {};
    if (!currentInstructionWh || !allowed.has(currentInstructionWh)) next.warehouse_id = fallbackWh;
    if (!currentBatchWh || !allowed.has(currentBatchWh)) next.batch_warehouse_id = fallbackWh;
    if (Object.keys(next).length > 0) {
      try { form.setFieldsValue(next); } catch {}
    }
  }, [isWarehouseScopedUser, warehouseOptionsForExecution, form]);
  const parseIocCodesFromSelector = useCallback((selector: any): string[] => {
    try {
      if (selector && typeof selector === 'object') {
        const arr = Array.isArray(selector?.ioc_codes) ? selector.ioc_codes : [];
        return arr.map((x: any) => String(x || '').trim()).filter(Boolean);
      }
      const s = String(selector || '').trim();
      if (!s) return [];
      try {
        const j = JSON.parse(s);
        const arr = Array.isArray(j?.ioc_codes) ? j.ioc_codes : [];
        return arr.map((x: any) => String(x || '').trim()).filter(Boolean);
      } catch {
        return [];
      }
    } catch {
      return [];
    }
  }, []);
  const countHistoryIocContainersFromRecord = useCallback((record: any): number => {
    try {
      const targets = Array.isArray(record?.targets_detail) ? record.targets_detail : [];
      const rows = targets.filter((t: any) => String(t?.target_type || '').trim() === 'instruction_output');
      if (rows.length === 0) return 0;
      const keys = new Set<string>();
      rows.forEach((t: any, idx: number) => {
        const snap = t?.snapshot || {};
        const pid = Number(t?.project_id ?? snap?.project_id);
        const projectKey = Number.isFinite(pid) ? String(pid) : '-';
        const containerId = String(snap?.output_container_id ?? snap?.container_id ?? '').trim();
        const selectorCodes = parseIocCodesFromSelector(t?.output_selector_obj ?? t?.output_selector);
        const snapCode = String(snap?.output_code || snap?.system_code || snap?.code || '').trim();
        if (containerId) {
          keys.add(`${projectKey}::id::${containerId}`);
        }
        selectorCodes.forEach((c: string) => {
          const code = String(c || '').trim().toUpperCase();
          if (code) keys.add(`${projectKey}::code::${code}`);
        });
        if (snapCode) keys.add(`${projectKey}::code::${snapCode.toUpperCase()}`);
        if (!containerId && selectorCodes.length === 0 && !snapCode) {
          keys.add(`${projectKey}::fallback::${idx}`);
        }
      });
      return keys.size;
    } catch {
      return 0;
    }
  }, [parseIocCodesFromSelector]);
  const normalizeOverviewIocCode = useCallback((v: any) => String(v || '').trim().toUpperCase(), []);
  const overviewContainerTypeTextMap: Record<string, string> = {
    box: '箱子',
    pallet: '托盘',
    crate: '周转箱',
    bag: '袋',
    envelope: '信封',
    other: '其他',
  };
  const countUniqueHistoryIocRows = useCallback((rows: any[]): number => {
    const arr = Array.isArray(rows) ? rows : [];
    if (arr.length === 0) return 0;
    const keys = new Set<string>();
    arr.forEach((r: any, idx: number) => {
      const pid = Number(r?.project_id);
      const projectKey = Number.isFinite(pid) ? String(pid) : '-';
      const id = String(r?.output_container_id || '').trim();
      const code = String(r?.output_code || r?.display_code || '').trim().toUpperCase();
      if (id) {
        keys.add(`${projectKey}::id::${id}`);
        return;
      }
      if (code) {
        keys.add(`${projectKey}::code::${code}`);
        return;
      }
      keys.add(`fallback::${idx}`);
    });
    return keys.size;
  }, []);
  const historyTargetsVisualRows = useMemo(() => {
    const rows = Array.isArray(historyTargetsRows) ? [...historyTargetsRows] : [];
    const parseCodeSortMeta = (row: any) => {
      const code = String(row?.output_code || row?.display_code || '').trim().toUpperCase();
      if (!code) return { groupNum: -1, tailNum: Number.MAX_SAFE_INTEGER };
      const parts = code.split('-');
      if (parts.length < 2) return { groupNum: -1, tailNum: Number.MAX_SAFE_INTEGER };
      const groupRaw = parts[parts.length - 2];
      const tailRaw = parts[parts.length - 1];
      const groupNum = Number(groupRaw);
      const tailNum = Number(tailRaw);
      return {
        groupNum: Number.isFinite(groupNum) ? groupNum : -1,
        tailNum: Number.isFinite(tailNum) ? tailNum : Number.MAX_SAFE_INTEGER,
      };
    };
    const codeOf = (r: any) => String(r?.output_code || r?.display_code || '').trim();
    const parentByCode = new Map<string, string>();
    const childByParent = new Map<string, Set<string>>();
    (rows || []).forEach((r: any) => {
      const code = codeOf(r);
      if (!code) return;
      const parentCode = String(r?.parent_code || '').trim();
      if (parentCode) parentByCode.set(code, parentCode);
      const childCodes = Array.isArray(r?.child_codes) ? r.child_codes : [];
      childCodes.map((x: any) => String(x || '').trim()).filter(Boolean).forEach((cc: string) => {
        const s = childByParent.get(code) || new Set<string>();
        s.add(cc);
        childByParent.set(code, s);
      });
    });
    parentByCode.forEach((p, c) => {
      const s = childByParent.get(p) || new Set<string>();
      s.add(c);
      childByParent.set(p, s);
    });
    const getRole = (r: any): 'parent' | 'child' | 'standalone' => {
      const code = codeOf(r);
      if (!code) return 'standalone';
      const parentCode = String(parentByCode.get(code) || r?.parent_code || '').trim();
      if (parentCode) return 'child';
      const hasChildren = (childByParent.get(code)?.size || 0) > 0
        || !!r?.is_parent_container
        || (Array.isArray(r?.child_codes) && r.child_codes.length > 0);
      if (hasChildren) return 'parent';
      return 'standalone';
    };
    rows.sort((a: any, b: any) => {
      const apc = String(a?.project_code || '').trim();
      const bpc = String(b?.project_code || '').trim();
      if (apc !== bpc) return apc.localeCompare(bpc, 'zh-Hans-CN');
      const apn = String(a?.project_name || '').trim();
      const bpn = String(b?.project_name || '').trim();
      if (apn !== bpn) return apn.localeCompare(bpn, 'zh-Hans-CN');
      const aid = Number(a?.project_id || 0);
      const bid = Number(b?.project_id || 0);
      if (aid !== bid) return aid - bid;
      const roleA = getRole(a);
      const roleB = getRole(b);
      const codeA = codeOf(a);
      const codeB = codeOf(b);
      const rootA = String(parentByCode.get(codeA) || a?.parent_code || codeA).trim();
      const rootB = String(parentByCode.get(codeB) || b?.parent_code || codeB).trim();
      const ga = parseCodeSortMeta({ output_code: rootA });
      const gb = parseCodeSortMeta({ output_code: rootB });
      if (ga.groupNum !== gb.groupNum) return ga.groupNum - gb.groupNum;
      if (ga.tailNum !== gb.tailNum) return ga.tailNum - gb.tailNum;
      const groupKeyA = roleA === 'standalone' ? `self::${rootA}` : `hier::${rootA}`;
      const groupKeyB = roleB === 'standalone' ? `self::${rootB}` : `hier::${rootB}`;
      if (groupKeyA !== groupKeyB) return groupKeyA.localeCompare(groupKeyB);
      const roleOrderA = roleA === 'parent' ? 0 : (roleA === 'child' ? 1 : 0);
      const roleOrderB = roleB === 'parent' ? 0 : (roleB === 'child' ? 1 : 0);
      if (roleOrderA !== roleOrderB) return roleOrderA - roleOrderB;
      const sa = parseCodeSortMeta({ output_code: codeA });
      const sb = parseCodeSortMeta({ output_code: codeB });
      if (sa.tailNum !== sb.tailNum) return sa.tailNum - sb.tailNum;
      return codeA.localeCompare(codeB);
    });
    return rows;
  }, [historyTargetsRows]);
  const historyTargetsVisualMeta = useMemo(() => {
    const rowRoleMap = new Map<string, 'parent' | 'child' | 'standalone'>();
    const rowGroupBorderMap = new Map<string, { grouped: boolean; top: boolean; bottom: boolean }>();
    const rowGroupKeyMap = new Map<string, string>();
    const groupCount = new Map<string, number>();
    const getRowKey = (r: any, i?: number) => String(r?.__key || r?.output_container_id || r?.output_code || i || '');
    const codeOf = (r: any) => String(r?.output_code || r?.display_code || '').trim();
    const parentByCode = new Map<string, string>();
    const childByParent = new Map<string, Set<string>>();
    (historyTargetsVisualRows || []).forEach((r: any) => {
      const code = codeOf(r);
      if (!code) return;
      const parentCode = String(r?.parent_code || '').trim();
      if (parentCode) parentByCode.set(code, parentCode);
      const childCodes = Array.isArray(r?.child_codes) ? r.child_codes : [];
      childCodes.map((x: any) => String(x || '').trim()).filter(Boolean).forEach((cc: string) => {
        const s = childByParent.get(code) || new Set<string>();
        s.add(cc);
        childByParent.set(code, s);
      });
    });
    parentByCode.forEach((p, c) => {
      const s = childByParent.get(p) || new Set<string>();
      s.add(c);
      childByParent.set(p, s);
    });
    const getRole = (r: any): 'parent' | 'child' | 'standalone' => {
      const code = codeOf(r);
      if (!code) return 'standalone';
      const parentCode = String(parentByCode.get(code) || r?.parent_code || '').trim();
      if (parentCode) return 'child';
      const hasChildren = (childByParent.get(code)?.size || 0) > 0
        || !!r?.is_parent_container
        || (Array.isArray(r?.child_codes) && r.child_codes.length > 0);
      if (hasChildren) return 'parent';
      return 'standalone';
    };
    (historyTargetsVisualRows || []).forEach((r: any, idx: number) => {
      const rowKey = getRowKey(r, idx);
      const code = codeOf(r);
      const role = getRole(r);
      const rootCode = String(parentByCode.get(code) || r?.parent_code || code).trim();
      const projectKey = String(Number.isFinite(Number(r?.project_id)) ? Number(r?.project_id) : (r?.project_code || ''));
      const groupKey = role === 'standalone' ? `${projectKey}|standalone:${rootCode}` : `${projectKey}|hier:${rootCode}`;
      rowRoleMap.set(rowKey, role);
      rowGroupKeyMap.set(rowKey, groupKey);
      groupCount.set(groupKey, (groupCount.get(groupKey) || 0) + 1);
    });
    (historyTargetsVisualRows || []).forEach((r: any, idx: number) => {
      const rowKey = getRowKey(r, idx);
      const gk = String(rowGroupKeyMap.get(rowKey) || '');
      const prevKey = idx > 0 ? getRowKey(historyTargetsVisualRows[idx - 1], idx - 1) : '';
      const nextKey = idx < historyTargetsVisualRows.length - 1 ? getRowKey(historyTargetsVisualRows[idx + 1], idx + 1) : '';
      const prevGk = prevKey ? String(rowGroupKeyMap.get(prevKey) || '') : '';
      const nextGk = nextKey ? String(rowGroupKeyMap.get(nextKey) || '') : '';
      const grouped = gk.includes('|hier:') && Number(groupCount.get(gk) || 0) > 1;
      rowGroupBorderMap.set(rowKey, {
        grouped,
        top: grouped && prevGk !== gk,
        bottom: grouped && nextGk !== gk,
      });
    });
    return { rowRoleMap, rowGroupBorderMap };
  }, [historyTargetsVisualRows]);
  const buildHistoryInputIocRowsForRecord = useCallback(async (record: any): Promise<any[]> => {
    try {
      const d = record || {};
      const list = Array.isArray(d?.targets_detail) ? d.targets_detail : [];
      const historyTargets = (list || []).filter((r:any) => String(r?.target_type || '').trim() === 'instruction_output');
      if (historyTargets.length === 0) return [];
      const projectById = new Map<number, { code?: string; name?: string }>();
      try {
        const prows = Array.isArray(d?.projects_detail) ? d.projects_detail : [];
        prows.forEach((p:any) => {
          const id = Number(p?.id);
          if (!Number.isFinite(id)) return;
          projectById.set(id, { code: p?.project_code, name: p?.name });
        });
      } catch {}
      const baseRows = historyTargets.map((r:any, idx:number) => {
        const snap = r?.snapshot || {};
        const selector = r?.output_selector_obj ?? r?.output_selector;
        const codes = parseIocCodesFromSelector(selector);
        const pid = Number(r?.project_id);
        const p = Number.isFinite(pid) ? projectById.get(pid) : undefined;
        return {
          __key: `todo-op-hoc-${String(r?.id || idx)}`,
          project_id: Number.isFinite(pid) ? pid : undefined,
          project_code: r?.project_code || snap?.project_code || p?.code,
          project_name: r?.project_name || snap?.project_name || p?.name,
          instruction_id: Number(r?.prev_instruction_id || snap?.prev_instruction_id || snap?.instruction_id || 0) || undefined,
          instruction_code: String(snap?.prev_instruction_code || snap?.instruction_code || '').trim() || undefined,
          output_container_id: snap?.output_container_id ?? snap?.container_id ?? undefined,
          output_code: String(codes?.[0] || snap?.output_code || snap?.system_code || snap?.code || '').trim(),
          display_code: String(codes?.[0] || snap?.output_code || snap?.system_code || snap?.code || '').trim(),
          container_type_display: snap?.container_type_display || snap?.container_type || '-',
          output_content_kind: snap?.output_content_kind || '',
          output_content_kind_display: snap?.output_content_kind_display || '-',
          output_contents_summary: snap?.output_contents_summary || '-',
          output_contents_detail: snap?.output_contents_detail || snap?.contents_detail || '',
          output_contents_direct_total_qty: Number(
            snap?.output_contents_direct_total_qty
            ?? snap?.direct_total_qty
            ?? snap?.output_contents_total_qty
            ?? snap?.total_qty
            ?? 0,
          ) || 0,
          output_contents_total_qty: Number(snap?.output_contents_total_qty || snap?.total_qty || 0) || 0,
          parent_container_id: snap?.parent_container_id,
          parent_code: String(snap?.parent_code || '').trim() || undefined,
          child_codes: Array.isArray(snap?.child_codes) ? snap.child_codes : [],
          is_parent_container: !!snap?.is_parent_container,
          is_child_container: !!snap?.is_child_container,
          length_cm: snap?.length_cm,
          width_cm: snap?.width_cm,
          height_cm: snap?.height_cm,
          weight_g: snap?.weight_g,
          created_at: snap?.created_at,
        } as any;
      });
      let mergedRows = baseRows;
      const prevIds: number[] = Array.from(new Set(
        (baseRows || []).map((x:any) => Number(x?.instruction_id)).filter((v:number) => Number.isFinite(v) && v > 0)
      ));
      if (prevIds.length > 0) {
        const detailByCode = new Map<string, any>();
        const detailById = new Map<string, any>();
        await Promise.all(prevIds.map(async (pid:number) => {
          try {
            const rr = await request.get(`/api/v1/instruction/instructions/${pid}/ioc/with-contents/`);
            const arr = Array.isArray(rr?.data?.results) ? rr.data.results : [];
            (arr || []).forEach((oc:any) => {
              const idKey = `${pid}::id::${String(oc?.id || '')}`;
              const codeKey = `${pid}::code::${normalizeOverviewIocCode(oc?.code)}`;
              if (String(oc?.id || '').trim()) detailById.set(idKey, oc);
              if (normalizeOverviewIocCode(oc?.code)) detailByCode.set(codeKey, oc);
            });
          } catch {}
        }));
        mergedRows = (baseRows || []).map((row:any) => {
          const pid = Number(row?.instruction_id);
          if (!Number.isFinite(pid) || pid <= 0) return row;
          const byId = row?.output_container_id ? detailById.get(`${pid}::id::${String(row.output_container_id)}`) : undefined;
          const byCode = detailByCode.get(`${pid}::code::${normalizeOverviewIocCode(row?.output_code || row?.display_code)}`);
          const hit = byId || byCode;
          if (!hit) return row;
          const contents = Array.isArray(hit?.contents) ? hit.contents : [];
          const totalQty = contents.reduce((acc:number, c:any) => acc + (Number(c?.quantity || 0) || 0), 0);
          const kind = String(hit?.content_kind || row?.output_content_kind || '').trim().toLowerCase();
          const contentKindDisplay = kind === 'exception' ? '异常内容容器' : '正常内容容器';
          const detailText = (contents || []).map((c:any) => {
            const code = String(c?.inventory_unit_system_barcode || c?.inventory_unit_id || '').trim();
            const qty = Number(c?.quantity || 0) || 0;
            return code ? `${code}×${qty}` : '';
          }).filter(Boolean).join('；');
          return {
            ...row,
            output_container_id: hit?.id ?? row?.output_container_id,
            output_code: String(hit?.code || row?.output_code || row?.display_code || '').trim(),
            display_code: String(hit?.code || row?.display_code || row?.output_code || '').trim(),
            container_type_display: row?.container_type_display && String(row.container_type_display).trim() !== '-'
              ? row.container_type_display
              : (String(hit?.container_type || '').trim() || '-'),
            output_content_kind: kind || row?.output_content_kind || '',
            output_content_kind_display: row?.output_content_kind_display && String(row.output_content_kind_display).trim() !== '-'
              ? row.output_content_kind_display
              : contentKindDisplay,
            output_contents_summary: row?.output_contents_summary && String(row.output_contents_summary).trim() !== '-'
              ? row.output_contents_summary
              : (contents.length > 0 ? `${contents.length}项` : '-'),
            output_contents_detail: String(row?.output_contents_detail || '').trim() || detailText,
            output_contents_direct_total_qty: Number(
              row?.output_contents_direct_total_qty
              ?? hit?.output_contents_direct_total_qty
              ?? row?.output_contents_total_qty
              ?? totalQty
              ?? 0,
            ) || 0,
            output_contents_total_qty: Number(row?.output_contents_total_qty || 0) > 0 ? Number(row.output_contents_total_qty || 0) : totalQty,
            parent_container_id: hit?.parent_container_id ?? row?.parent_container_id,
            parent_code: String(hit?.parent_code || row?.parent_code || '').trim() || undefined,
            child_codes: Array.isArray(hit?.child_codes) ? hit.child_codes : (Array.isArray(row?.child_codes) ? row.child_codes : []),
            is_parent_container: hit?.is_parent_container != null ? !!hit.is_parent_container : !!row?.is_parent_container,
            is_child_container: hit?.is_child_container != null ? !!hit.is_child_container : !!row?.is_child_container,
            length_cm: hit?.length_cm ?? row?.length_cm,
            width_cm: hit?.width_cm ?? row?.width_cm,
            height_cm: hit?.height_cm ?? row?.height_cm,
            weight_g: hit?.weight_g ?? row?.weight_g,
            created_at: hit?.created_at ?? row?.created_at,
          };
        });
      }
      try {
        const projectIds: number[] = Array.from(new Set(
          (mergedRows || [])
            .map((r:any) => Number(r?.project_id))
            .filter((v:number) => Number.isFinite(v) && v > 0)
        ));
        if (projectIds.length > 0) {
          const warehouseCode = String(d?.warehouse_detail?.code || d?.warehouse?.code || '').trim();
          const unifiedByCode = new Map<string, any>();
          await Promise.all(projectIds.map(async (pid:number) => {
            try {
              const params: any = {
                page: 1,
                page_size: 500,
                container_kind: 'output',
                project: String(pid),
                instruction_id: Number(d?.id || 0) || undefined,
                ordering: '-created_at',
              };
              if (warehouseCode) params.warehouse_code = warehouseCode;
              const resp = await request.get('/api/v1/storage/containers/unified/', { params });
              const rows = Array.isArray(resp?.data?.results) ? resp.data.results : [];
              (rows || []).forEach((r:any) => {
                const code = normalizeOverviewIocCode(r?.system_code || r?.display_code || '');
                if (code) unifiedByCode.set(`${pid}::${code}`, r);
              });
            } catch {}
          }));
          mergedRows = (mergedRows || []).map((row:any) => {
            const pid = Number(row?.project_id);
            const code = normalizeOverviewIocCode(row?.output_code || row?.display_code);
            if (!Number.isFinite(pid) || pid <= 0 || !code) return row;
            const hit = unifiedByCode.get(`${pid}::${code}`);
            if (!hit) return row;
            return {
              ...row,
              output_code: String(hit?.system_code || hit?.display_code || row?.output_code || row?.display_code || '').trim(),
              display_code: String(hit?.display_code || hit?.system_code || row?.display_code || row?.output_code || '').trim(),
              container_type_display: hit?.container_type_display || row?.container_type_display || '-',
              output_content_kind: hit?.output_content_kind || row?.output_content_kind || '',
              output_content_kind_display: hit?.output_content_kind_display || row?.output_content_kind_display || '-',
              output_contents_summary: hit?.output_contents_summary || row?.output_contents_summary || '-',
              output_contents_detail: hit?.output_contents_detail || row?.output_contents_detail || '',
              output_contents_direct_total_qty: Number(
                hit?.output_contents_direct_total_qty
                ?? row?.output_contents_direct_total_qty
                ?? hit?.output_contents_total_qty
                ?? row?.output_contents_total_qty
                ?? 0,
              ) || 0,
              output_contents_total_qty: Number(hit?.output_contents_total_qty || row?.output_contents_total_qty || 0) || 0,
              parent_container_id: hit?.parent_container_id ?? row?.parent_container_id,
              parent_code: String(hit?.parent_code || row?.parent_code || '').trim() || undefined,
              child_codes: Array.isArray(hit?.child_codes) ? hit.child_codes : (Array.isArray(row?.child_codes) ? row.child_codes : []),
              is_parent_container: hit?.is_parent_container != null ? !!hit.is_parent_container : !!row?.is_parent_container,
              is_child_container: hit?.is_child_container != null ? !!hit.is_child_container : !!row?.is_child_container,
            };
          });
        }
      } catch {}
      // 再补一层层级关系：确保 parent/child 信息稳定可用（用于“层级标识 + 同组边框”）
      try {
        const instrIds: number[] = Array.from(new Set(
          (mergedRows || [])
            .map((r:any) => Number(r?.instruction_id))
            .filter((v:number) => Number.isFinite(v) && v > 0),
        ));
        if (instrIds.length > 0) {
          const hierByInstrCode = new Map<string, any>();
          await Promise.all(instrIds.map(async (pid:number) => {
            try {
              const resp = await request.get('/api/v1/storage/output-containers/', {
                params: { instruction_id: pid, page: 1, page_size: 500, ordering: '-created_at' },
              });
              const rows = Array.isArray(resp?.data?.results) ? resp.data.results : [];
              (rows || []).forEach((x:any) => {
                const code = normalizeOverviewIocCode(x?.code || x?.system_code || x?.display_code || '');
                if (!code) return;
                hierByInstrCode.set(`${pid}::${code}`, {
                  parent_code: String(x?.parent_code || '').trim() || undefined,
                  child_codes: Array.isArray(x?.child_codes) ? x.child_codes.map((c:any) => String(c || '').trim()).filter(Boolean) : [],
                  is_parent_container: !!x?.is_parent_container || (Array.isArray(x?.child_codes) && x.child_codes.length > 0),
                  is_child_container: !!x?.is_child_container || !!x?.parent_container_id || !!x?.parent_code,
                  parent_container_id: x?.parent_container_id,
                });
              });
            } catch {}
          }));
          if (hierByInstrCode.size > 0) {
            mergedRows = (mergedRows || []).map((row:any) => {
              const pid = Number(row?.instruction_id);
              const code = normalizeOverviewIocCode(row?.output_code || row?.display_code);
              if (!Number.isFinite(pid) || pid <= 0 || !code) return row;
              const hit = hierByInstrCode.get(`${pid}::${code}`);
              if (!hit) return row;
              return {
                ...row,
                parent_container_id: hit?.parent_container_id ?? row?.parent_container_id,
                parent_code: String(hit?.parent_code || row?.parent_code || '').trim() || undefined,
                child_codes: Array.isArray(hit?.child_codes) && hit.child_codes.length > 0
                  ? hit.child_codes
                  : (Array.isArray(row?.child_codes) ? row.child_codes : []),
                is_parent_container: hit?.is_parent_container != null ? !!hit.is_parent_container : !!row?.is_parent_container,
                is_child_container: hit?.is_child_container != null ? !!hit.is_child_container : !!row?.is_child_container,
              };
            });
          }
        }
      } catch {}
      return mergedRows;
    } catch {
      return [];
    }
  }, [normalizeOverviewIocCode, parseIocCodesFromSelector]);
  const navigate = useNavigate();
  const location = useLocation();
  const pageStateTimerRef = useRef<any>(null);
  const currentTabId = useMemo(() => {
    try {
      const sp = new URLSearchParams(location.search || '');
      return String(sp.get('tabId') || '').trim() || 'default';
    } catch {
      return 'default';
    }
  }, [location.search]);
  const pageStateStorageKey = useMemo(() => `todo_page_state_${currentTabId}`, [currentTabId]);

  useEffect(() => {
    setPageStateReady(false);
    try {
      const sp = new URLSearchParams(location.search || '');
      const urlMode = String(sp.get('mode') || '').trim();
      const urlTab = String(sp.get('tab') || '').trim();
      const hasUrlMode = urlMode === 'instruction' || urlMode === 'internal' || urlMode === 'batch';
      const hasUrlTab = urlTab === 'todo' || urlTab === 'mine' || urlTab === 'done';
      const shouldSkipFormRestore = hasUrlMode || hasUrlTab;

      const raw = sessionStorage.getItem(pageStateStorageKey) || localStorage.getItem(pageStateStorageKey);
      if (!raw) {
        if (shouldSkipFormRestore) {
          try { form.resetFields(); } catch {}
          try { setPager(prev => ({ ...prev, current: 1, pageSize: 10 })); } catch {}
        }
        if (hasUrlMode) setMode(urlMode as any);
        if (hasUrlTab) setTab(urlTab as any);
        setPageStateReady(true);
        return;
      }
      const parsed = JSON.parse(raw);
      const m = String(parsed?.mode || '').trim();
      const t = String(parsed?.tab || '').trim();
      const p = parsed?.pager || {};
      if (hasUrlMode) setMode(urlMode as any);
      else if (m === 'instruction' || m === 'internal' || m === 'batch') setMode(m as any);
      if (hasUrlTab) setTab(urlTab as any);
      else if (t === 'todo' || t === 'mine' || t === 'done') setTab(t as any);
      const nextCurrent = Number(p?.current || 1);
      const nextPageSize = Number(p?.pageSize || 10);
      if (!shouldSkipFormRestore && (Number.isFinite(nextCurrent) || Number.isFinite(nextPageSize))) {
        setPager(prev => ({
          ...prev,
          current: Number.isFinite(nextCurrent) && nextCurrent > 0 ? nextCurrent : prev.current,
          pageSize: Number.isFinite(nextPageSize) && nextPageSize > 0 ? nextPageSize : prev.pageSize,
        }));
      }
      if (shouldSkipFormRestore) {
        try { form.resetFields(); } catch {}
        try { setPager(prev => ({ ...prev, current: 1, pageSize: 10 })); } catch {}
      }
      if (!shouldSkipFormRestore && parsed?.formValues && typeof parsed.formValues === 'object') {
        try { form.setFieldsValue(parsed.formValues); } catch {}
      }
    } catch {
      // ignore restore errors
    } finally {
      setPageStateReady(true);
    }
  }, [pageStateStorageKey, form, location.search]);

  useEffect(() => {
    if (!pageStateReady) return;
    try { if (pageStateTimerRef.current) clearTimeout(pageStateTimerRef.current); } catch {}
    pageStateTimerRef.current = setTimeout(() => {
      try {
        const stateToSave = {
          mode,
          tab,
          pager: { current: Number(pager.current || 1), pageSize: Number(pager.pageSize || 10) },
          formValues: form.getFieldsValue(true),
          savedAt: Date.now(),
        };
        sessionStorage.setItem(pageStateStorageKey, JSON.stringify(stateToSave));
      } catch {}
    }, 220);
    return () => {
      try { if (pageStateTimerRef.current) clearTimeout(pageStateTimerRef.current); } catch {}
    };
  }, [pageStateReady, pageStateStorageKey, mode, tab, pager.current, pager.pageSize, form, formValuesWatch]);

  // 通用：按页聚合获取全部工单（兼容 {results,count} 与直接数组）
  const fetchAllWorkOrders = async (paramsBase: Record<string, any>) => {
    const pageSize = 200;
    let page = 1;
    const all: any[] = [];
    while (true) {
      const resp = await request.get('/api/v1/workorders/workorders/', { params: { ...paramsBase, page, page_size: pageSize } });
      const data = resp?.data;
      if (Array.isArray(data)) {
        // 未分页返回，视为全量
        return data;
      }
      const results = Array.isArray(data?.results) ? data.results : [];
      all.push(...results);
      if (results.length < pageSize) break;
      page += 1;
    }
    return all;
  };

  // 通用：按页聚合获取全部执行任务
  const fetchAllExecTasks = async (paramsBase: Record<string, any>) => {
    const pageSize = 200;
    let page = 1;
    const all: any[] = [];
    while (true) {
      const resp = await request.get('/api/v1/instruction/exec/tasks/', { params: { ...paramsBase, page, page_size: pageSize } });
      const data = resp?.data;
      const results = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
      all.push(...results);
      if (!Array.isArray(data?.results) || results.length < pageSize) break;
      page += 1;
    }
    return all;
  };

  // 后端增强：查询“属于有效批次”的指令ID，用于单指令tab去重
  const fetchBatchedInstructionIdSet = async (instructionIds: number[]) => {
    try {
      const ids = Array.from(new Set((instructionIds || []).map((x) => Number(x)).filter((x) => Number.isFinite(x))));
      if (!ids.length) return new Set<number>();
      const resp = await request.post('/api/v1/instruction/instructions/batched-instruction-ids/', {
        instruction_ids: ids,
      });
      const arr = Array.isArray(resp?.data?.instruction_ids) ? resp.data.instruction_ids : [];
      return new Set<number>(arr.map((x:any) => Number(x)).filter((x:any) => Number.isFinite(x)));
    } catch {
      return new Set<number>();
    }
  };

  const loadInstruction = async (targetTab: 'todo'|'mine'|'done' = tab, silent = false) => {
    if (!silent) setLoading(true);
    try {
      // 三个页签来源不同：
      // - 待领取：改为查执行任务 ExecutionTask status=pending，再回填指令详情用于展示与筛选
      // - 我在执行：按工单 assignee_user_id=我 & status in [accepted,in_progress]，再回填到指令
      // - 我已完成：按工单 assignee_user_id=我 & status=completed，回填到指令
      if (targetTab === 'todo') {
        // 1) 拉取执行任务（全量）
        const etList = await fetchAllExecTasks({ status: 'pending' });
        // 将“执行任务”映射到“指令ID -> 待领取任务ID”（用于后续领取 API）
        const pendingTaskByInstr = new Map<number, number>();
        try {
          (etList || []).forEach((et:any) => {
            const instrId = Number(et?.instruction);
            const taskId = Number(et?.id);
            if (Number.isFinite(instrId) && Number.isFinite(taskId)) {
              if (!pendingTaskByInstr.has(instrId)) pendingTaskByInstr.set(instrId, taskId);
            }
          });
        } catch {}
        let instrIds: number[] = Array.from(new Set(
          etList.map((x:any) => x?.instruction).filter((v:any) => v != null).map((v:any) => Number(v))
        )) as number[];
        // 去重：已在有效批次中的指令不显示在“单指令执行”tab
        try {
          const batchedSet = await fetchBatchedInstructionIdSet(instrIds);
          instrIds = instrIds.filter((id) => !batchedSet.has(Number(id)));
        } catch {}
        if (instrIds.length === 0) {
          if (!silent) {
            setRows([]);
            try { setPager(prev => ({ ...prev, total: 0, current: 1 })); } catch {}
          }
          return [];
        }
        // 2) 获取指令详情集合
        const instrPromises = instrIds.map((id:number) => request.get(`/api/v1/instruction/instructions/${id}/`).then(r=>r?.data).catch(()=>null));
        let list = (await Promise.all(instrPromises)).filter(Boolean).map((r:any) => {
          try {
            const id = Number(r?.id);
            const taskId = Number(pendingTaskByInstr.get(id));
            return { ...r, _pending_task_id: (Number.isFinite(taskId) ? taskId : undefined) };
          } catch { return r; }
        });
        // 3) 应用筛选（基于指令字段）
        const fv = form.getFieldsValue();
        const sysCode = (fv?.system_code || '').toString().trim();
        const nameKw = (fv?.keyword || '').toString().trim().toLowerCase();
        const projId = fv?.project_id ? String(fv.project_id) : '';
        const whId = fv?.warehouse_id ? String(fv.warehouse_id) : '';
        const pri = (fv?.priority || '').toString().trim().toLowerCase();
        const tplCodeSel = (fv?.template_code || '').toString().trim();
        if (sysCode) {
          list = list.filter((r:any) => String(r?.system_code || '').toLowerCase().includes(sysCode.toLowerCase()));
        }
        if (nameKw) {
          list = list.filter((r:any) => {
            const n = (r?.name || '').toString().toLowerCase();
            const rm = (r?.remarks || '').toString().toLowerCase();
            return n.includes(nameKw) || rm.includes(nameKw);
          });
        }
        if (projId) {
          list = list.filter((r:any) => String(r?.project_id || '') === projId);
        }
        if (whId) {
          list = list.filter((r:any) => String(r?.warehouse_id || '') === whId);
        }
        if (pri) {
          list = list.filter((r:any) => String(r?.priority || '').toLowerCase() === pri);
        }
        if (tplCodeSel) {
          list = list.filter((r:any) => {
            try {
              const snap = r?.template_snapshot || {};
              const detail = r?.template_detail || {};
              const code = (snap.system_code || detail.system_code || r?.template_system_code || r?.template || '').toString();
              return code === tplCodeSel;
            } catch { return false; }
          });
        }
        // 4) 仅展示“已受理但尚未开始执行”的待领取指令，避免与“执行中”重复
        try {
          list = list.filter((r:any) => String(r?.status) === 'accepted');
        } catch {}
        // 5) 自定义排序：优先级（super>high>normal）→ 审核时间（旧在前）
        const weight = (v: any) => {
          const s = String(v || '').toLowerCase();
          if (s === 'super') return 3;
          if (s === 'high') return 2;
          if (s === 'normal') return 1;
          return 0;
        };
        const toTime = (v: any) => {
          const dt = v ? new Date(v as any).getTime() : Number.POSITIVE_INFINITY;
          return isNaN(dt) ? Number.POSITIVE_INFINITY : dt;
        };
        const sorted = [...list].sort((a:any, b:any) => {
          const wa = weight(a?.priority);
          const wb = weight(b?.priority);
          if (wa !== wb) return wb - wa;
          const ta = toTime(a?.reviewed_at);
          const tb = toTime(b?.reviewed_at);
          return ta - tb;
        });
        if (!silent) {
          setRows(sorted);
          try { setPager(prev => ({ ...prev, total: sorted.length, current: 1 })); } catch {}
        }
        return sorted;
      }
      const uid = user?.id;
      const isAdmin = String(user?.role || '').toLowerCase() === 'admin';
      if (!uid && !isAdmin) {
        if (!silent) {
          setRows([]);
          try { setPager(prev => ({ ...prev, total: 0, current: 1 })); } catch {}
        }
        return [];
      }
      // 统一基于 ExecutionTask：mine=执行中，done=已完成
      const etStatus = targetTab === 'mine' ? 'in_progress' : 'completed';
      const etListAll = await fetchAllExecTasks({ status: etStatus });
      let etList = etListAll;
      if (!isAdmin) {
        // 员工端不再按个人 assignee 限制，统一显示“其权限范围内”的执行中/已完成。
        // 具体权限范围由后端接口（ExecutionTask / Instruction）按仓库员工可见范围收敛。
        etList = etListAll;
      }
      let instrIds = Array.from(new Set((etList || []).map((x:any) => Number(x?.instruction)).filter((v:any) => Number.isFinite(v))));
      // 去重：已在有效批次中的指令不显示在“单指令执行”tab
      try {
        const batchedSet = await fetchBatchedInstructionIdSet(instrIds as number[]);
        instrIds = (instrIds as number[]).filter((id:number) => !batchedSet.has(Number(id)));
      } catch {}
      if (instrIds.length === 0) {
        if (!silent) {
          setRows([]);
          try { setPager(prev => ({ ...prev, total: 0, current: 1 })); } catch {}
        }
        return;
      }
      // 逐条获取指令详情（后端列表暂不支持 id__in）
      const instrPromises = instrIds.map((id:number) =>
        request.get(`/api/v1/instruction/instructions/${id}/`).then(r => r?.data).catch(() => null),
      );
      let instrs = (await Promise.all(instrPromises)).filter(Boolean);
      // 本地过滤（mine/done 同样应用）
      const fv = form.getFieldsValue();
      const sysCode = (fv?.system_code || '').toString().trim();
      const nameKw = (fv?.keyword || '').toString().trim().toLowerCase();
      const projId = fv?.project_id ? String(fv.project_id) : '';
      const whId = fv?.warehouse_id ? String(fv.warehouse_id) : '';
      const pri = (fv?.priority || '').toString().trim().toLowerCase();
      const tplCodeSel = (fv?.template_code || '').toString().trim();
      if (sysCode) {
        instrs = instrs.filter((r:any) => String(r?.system_code || '').toLowerCase().includes(sysCode.toLowerCase()));
      }
      if (nameKw) {
        instrs = instrs.filter((r:any) => {
          const n = (r?.name || '').toString().toLowerCase();
          const rm = (r?.remarks || '').toString().toLowerCase();
          return n.includes(nameKw) || rm.includes(nameKw);
        });
      }
      if (projId) {
        instrs = instrs.filter((r:any) => String(r?.project_id || '') === projId);
      }
      if (whId) {
        instrs = instrs.filter((r:any) => String(r?.warehouse_id || '') === whId);
      }
      if (pri) {
        instrs = instrs.filter((r:any) => String(r?.priority || '').toLowerCase() === pri);
      }
      if (tplCodeSel) {
        instrs = instrs.filter((r:any) => {
          try {
            const snap = r?.template_snapshot || {};
            const detail = r?.template_detail || {};
            const code = (snap.system_code || detail.system_code || r?.template_system_code || r?.template || '').toString();
            return code === tplCodeSel;
          } catch { return false; }
        });
      }
      // done 页签仅展示“可终审口径”的执行完成态，排除驳回/取消等非终审指令
      if (targetTab === 'done') {
        instrs = instrs.filter((r: any) => {
          const s = String(r?.status || '').toLowerCase();
          return s === 'operation_done' || s === 'completed';
        });
      }
      if (!silent) {
        setRows(instrs);
        try { setPager(prev => ({ ...prev, total: instrs.length, current: 1 })); } catch {}
      }
      return instrs;
    } catch {
      if (!silent) {
        message.error('加载失败');
        setRows([]);
        try { setPager(prev => ({ ...prev, total: 0, current: 1 })); } catch {}
      }
      return [];
    } finally { if (!silent) setLoading(false); }
  };

  // 仓内工单加载
  const loadInternal = async (targetTab: 'todo'|'mine'|'done' = tab, silent = false) => {
    if (!silent) setLoading(true);
    try {
      if (targetTab === 'todo') {
        const list = await fetchAllWorkOrders({ category: 'internal', status: 'pending' });
        if (!silent) {
          setRows(list);
          try { setPager(prev => ({ ...prev, total: list.length, current: 1 })); } catch {}
        }
        return list;
      }
      const statusList = targetTab === 'mine' ? ['accepted', 'in_progress'] : ['completed'];
      // 不再按 assignee_user_id 限制，统一显示当前用户权限范围内的仓内工单执行中/已完成
      const reqs = statusList.map(st => fetchAllWorkOrders({ category: 'internal', status: st }).catch(() => []));
      const arr = (await Promise.all(reqs)).flat();
      if (!silent) {
        setRows(arr);
        try { setPager(prev => ({ ...prev, total: arr.length, current: 1 })); } catch {}
      }
      return arr;
    } catch {
      if (!silent) {
        message.error('加载失败');
        setRows([]);
        try { setPager(prev => ({ ...prev, total: 0, current: 1 })); } catch {}
      }
      return [];
    } finally { if (!silent) setLoading(false); }
  };

  // 批次执行加载（仅一件代发）
  const fetchAllBatches = async (paramsBase: Record<string, any>) => {
    const pageSize = 200;
    let page = 1;
    const all: any[] = [];
    while (true) {
      const resp = await request.get('/api/v1/instruction/batches/', { params: { ...paramsBase, page, page_size: pageSize } });
      const data = resp?.data;
      const results = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
      all.push(...results);
      if (!Array.isArray(data?.results) || results.length < pageSize) break;
      page += 1;
    }
    return all;
  };
  const loadBatch = async (
    targetTab: 'todo'|'mine'|'done' = tab,
    silent = false,
    opts?: { ignoreFormFilters?: boolean },
  ) => {
    if (!silent) setLoading(true);
    try {
      const status = targetTab === 'todo' ? 'precheck_done' : (targetTab === 'mine' ? 'executing' : 'executed');
      const list = await fetchAllBatches({ status });
      let dropship = (list || []).filter((r:any) => String(r?.template_system_code || '') === 'InstructionTemplate-SYS-19');

      // 批次筛选：系统编码 / 所含指令系统编码 / 仓库
      const fv = opts?.ignoreFormFilters ? {} : form.getFieldsValue();
      const batchCodeKw = String(fv?.batch_system_code || '').trim().toLowerCase();
      const instrCodeKw = String(fv?.batch_instruction_system_code || '').trim().toLowerCase();
      const warehouseId = fv?.batch_warehouse_id ? String(fv.batch_warehouse_id) : '';

      const extractInstructionCodes = (r: any): string[] => {
        try {
          const out = new Set<string>();
          const arr = Array.isArray(r?.items) ? r.items : [];
          arr.forEach((it: any) => {
            const cands = [
              it?.instruction_system_code,
              it?.system_code,
              it?.instruction_code,
              it?.instruction?.system_code,
              it?.instruction?.code,
            ];
            cands.forEach((v: any) => {
              const s = String(v || '').trim();
              if (s) out.add(s);
            });
          });
          const extra = Array.isArray(r?.instruction_system_codes) ? r.instruction_system_codes : [];
          extra.forEach((v: any) => {
            const s = String(v || '').trim();
            if (s) out.add(s);
          });
          return Array.from(out);
        } catch { return []; }
      };

      const extractWarehouseIds = (r: any): string[] => {
        try {
          const out = new Set<string>();
          const direct = [r?.warehouse_id, r?.warehouse?.id, r?.warehouse];
          direct.forEach((v: any) => {
            const s = String(v || '').trim();
            if (s && s !== '[object Object]') out.add(s);
          });
          const arr = Array.isArray(r?.items) ? r.items : [];
          arr.forEach((it: any) => {
            const cands = [
              it?.warehouse_id,
              it?.warehouse?.id,
              it?.instruction_warehouse_id,
              it?.instruction?.warehouse_id,
            ];
            cands.forEach((v: any) => {
              const s = String(v || '').trim();
              if (s && s !== '[object Object]') out.add(s);
            });
          });
          return Array.from(out);
        } catch { return []; }
      };

      if (batchCodeKw) {
        dropship = dropship.filter((r:any) => String(r?.system_code || '').toLowerCase().includes(batchCodeKw));
      }
      if (instrCodeKw) {
        dropship = dropship.filter((r:any) => extractInstructionCodes(r).some((c) => String(c).toLowerCase().includes(instrCodeKw)));
      }
      if (warehouseId) {
        dropship = dropship.filter((r:any) => extractWarehouseIds(r).includes(warehouseId));
      }

      if (!silent) {
        setRows(dropship);
        try { setPager(prev => ({ ...prev, total: dropship.length, current: 1 })); } catch {}
      }
      return dropship;
    } catch {
      if (!silent) {
        message.error('加载失败');
        setRows([]);
        try { setPager(prev => ({ ...prev, total: 0, current: 1 })); } catch {}
      }
      return [];
    } finally { if (!silent) setLoading(false); }
  };

  const refreshCountsForMode = async (targetMode: 'instruction'|'internal'|'batch') => {
    try {
      if (targetMode === 'instruction') {
        const [todoRows, mineRows, doneRows] = await Promise.all([
          loadInstruction('todo', true),
          loadInstruction('mine', true),
          loadInstruction('done', true),
        ]);
        setCountByModeTab((prev) => ({
          ...prev,
          'instruction:todo': Array.isArray(todoRows) ? todoRows.length : 0,
          'instruction:mine': Array.isArray(mineRows) ? mineRows.length : 0,
          'instruction:done': Array.isArray(doneRows) ? doneRows.length : 0,
        }));
        return;
      }
      if (targetMode === 'internal') {
        const [todoRows, mineRows, doneRows] = await Promise.all([
          loadInternal('todo', true),
          loadInternal('mine', true),
          loadInternal('done', true),
        ]);
        setCountByModeTab((prev) => ({
          ...prev,
          'internal:todo': Array.isArray(todoRows) ? todoRows.length : 0,
          'internal:mine': Array.isArray(mineRows) ? mineRows.length : 0,
          'internal:done': Array.isArray(doneRows) ? doneRows.length : 0,
        }));
        return;
      }
      const [todoRows, mineRows, doneRows] = await Promise.all([
        loadBatch('todo', true, { ignoreFormFilters: true }),
        loadBatch('mine', true, { ignoreFormFilters: true }),
        loadBatch('done', true, { ignoreFormFilters: true }),
      ]);
      setCountByModeTab((prev) => ({
        ...prev,
        'batch:todo': Array.isArray(todoRows) ? todoRows.length : 0,
        'batch:mine': Array.isArray(mineRows) ? mineRows.length : 0,
        'batch:done': Array.isArray(doneRows) ? doneRows.length : 0,
      }));
    } catch {
      // ignore count refresh errors
    }
  };

  useEffect(() => {
    if (!pageStateReady) return;
    if (mode === 'instruction') loadInstruction().finally(() => refreshCountsForMode('instruction'));
    else if (mode === 'internal') loadInternal().finally(() => refreshCountsForMode('internal'));
    else loadBatch().finally(() => refreshCountsForMode('batch'));
  }, [tab, mode, pageStateReady]);

  useEffect(() => {
    if (!pageStateReady) return;
    refreshCountsForMode(mode);
  }, [mode, pageStateReady]);

  useEffect(() => {
    if (!pageStateReady) return;
    // 首次进入页面即预热三大主tab计数，避免初始均为0
    refreshCountsForMode('batch');
    refreshCountsForMode('instruction');
    refreshCountsForMode('internal');
  }, [pageStateReady]);

  const handleModeChange = useCallback((v: any) => {
    const next = v as 'instruction'|'internal'|'batch';
    setMode(next);
    try { setTab('todo'); } catch {}
    try { setPager(prev => ({ ...prev, current: 1 })); } catch {}
  }, []);

  const handleSubTabChange = useCallback((k: string) => {
    setTab(k as any);
    try { setPager(prev => ({ ...prev, current: 1 })); } catch {}
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (!historyTargetsOf) {
          setHistoryTargetsRows([]);
          setHistoryTargetsLoading(false);
          return;
        }
        const id = Number(historyTargetsOf?.id || 0);
        const key = String(id || '');
        if (key && Array.isArray(historyInputIocRowsCacheByInstructionId[key])) {
          setHistoryTargetsRows(historyInputIocRowsCacheByInstructionId[key] || []);
          setHistoryTargetsLoading(false);
          return;
        }
        setHistoryTargetsLoading(true);
        const rows = await buildHistoryInputIocRowsForRecord(historyTargetsOf);
        if (cancelled) return;
        setHistoryTargetsRows(rows || []);
        if (key) {
          setHistoryInputIocRowsCacheByInstructionId((prev) => ({ ...prev, [key]: rows || [] }));
        }
      } catch {
        if (!cancelled) setHistoryTargetsRows([]);
      } finally {
        if (!cancelled) setHistoryTargetsLoading(false);
      }
    };
    void run();
    return () => { cancelled = true; };
  }, [historyTargetsOf, historyInputIocRowsCacheByInstructionId, buildHistoryInputIocRowsForRecord]);

  const iuTargetsRows = useMemo(() => (
    Array.isArray(targetsOf?.targets_detail) ? targetsOf.targets_detail : []
  ), [targetsOf?.targets_detail]);
  const [iuTargetsPage, setIuTargetsPage] = useState(1);
  const [iuTargetsPageSize, setIuTargetsPageSize] = useState(10);
  const [iuUnitContentCache, setIuUnitContentCache] = useState<Record<string, Array<{ barcode: string; qty: number; productName?: string; customCode?: string }>>>({});
  const [iuUnitTypeCache, setIuUnitTypeCache] = useState<Record<string, string>>({});
  const [iuUnitSortingGroupCache, setIuUnitSortingGroupCache] = useState<Record<string, { code: string; name: string }>>({});
  useEffect(() => { setIuTargetsPage(1); }, [targetsOf]);
  const iuProjectCount = useMemo(() => {
    try {
      const set = new Set<string>();
      (iuTargetsRows || []).forEach((r:any) => {
        const pid = r?.project_id ?? r?.target?.project_id;
        if (pid != null) { set.add(String(pid)); return; }
        const pcode = r?.project_code || r?.target?.snapshot?.project_code;
        if (pcode) set.add(`code:${String(pcode)}`);
      });
      return set.size;
    } catch { return 0; }
  }, [iuTargetsRows]);
  const iuEstimatedTotal = useMemo(() => (
    (iuTargetsRows || []).reduce((acc: number, t: any) => {
      const v = Number(t?.expected_qty ?? 0);
      return acc + (isNaN(v) ? 0 : v);
    }, 0)
  ), [iuTargetsRows]);
  const iuTotalKinds = iuTargetsRows.length;
  const iuAllKinds = useMemo(() => iuTargetsRows.filter((r:any)=> !!r?.use_all_available).length, [iuTargetsRows]);
  const iuPartKinds = Math.max(0, iuTotalKinds - iuAllKinds);
  const iuProjectMap = useMemo(() => new Map<string, any>(projects.map((p:any) => [String(p.id), p])), [projects]);
  const iuUnitTypeToReadable = useCallback((v?: string) => {
    const key = String(v || '').toUpperCase();
    const map: Record<string, string> = {
      LIU: '散货库存单元',
      BIU: '箱库存单元',
      PIU: '托盘库存单元',
      AIU: '异常库存单元',
      NIU: '非产品库存单元',
    };
    return map[key] || '';
  }, []);
  const iuGetUnitTypeFromRow = useCallback((row: any): string | '' => {
    try {
      const snap = row?.snapshot || {};
      const direct = snap?.unit_type || snap?.inventory_unit?.unit_type || row?.unit_type || row?.inventory_unit?.unit_type;
      if (direct) return String(direct);
    } catch {}
    try {
      if (row?.inventory_unit_id != null) {
        const key = String(row.inventory_unit_id);
        const cached = iuUnitTypeCache[key];
        if (cached) return cached;
      }
    } catch {}
    return '';
  }, [iuUnitTypeCache]);
  const iuRenderTargetTypeReadable = useCallback((row: any) => {
    const v = String(row?.target_type || '').trim();
    if (v === 'inventory_unit') {
      const ut = iuUnitTypeToReadable(iuGetUnitTypeFromRow(row));
      return ut ? ut : '库存单元';
    }
    const map: Record<string, string> = {
      inventory_unit: '库存单元',
      sorting_group: '分拣组',
      project_all: '项目内全部',
      instruction_output: '上游指令产出',
    };
    return map[v] || v || '-';
  }, [iuGetUnitTypeFromRow, iuUnitTypeToReadable]);
  const iuRenderProjectReadable = useCallback((row: any) => {
    const pid = row?.project_id != null ? String(row.project_id) : '';
    const p = pid ? iuProjectMap.get(pid) : null;
    if (p) return `${p.project_code || p.code || p.id} - ${p.name || '-'}`;
    const snap = row?.snapshot || {};
    const code = snap.project_code || snap.code || pid;
    const name = snap.project_name || snap.name || '';
    if (code || name) return `${code}${name ? ' - ' + name : ''}`;
    return '-';
  }, [iuProjectMap]);
  const iuRenderInventoryUnitReadable = useCallback((row: any) => {
    const snap = row?.snapshot || {};
    const code = snap.system_barcode
      || row?.inventory_unit_system_barcode
      || row?.inventory_unit?.system_barcode
      || snap.system_code
      || snap.barcode
      || snap.code
      || '';
    if (code) return code;
    const id = row?.inventory_unit_id;
    return id != null ? String(id) : '-';
  }, []);
  const iuExtractUnitContentItems = useCallback((row: any): Array<{ barcode: string; qty: number; productName?: string; customCode?: string }> => {
    try {
      const snap = row?.snapshot || {};
      const candidates: any[] = [
        snap?.content?.items,
        row?.content?.items,
        row?.inventory_unit?.content?.items,
        (snap?.content && Array.isArray(snap.content) ? snap.content : null),
        row?.items,
      ].filter(Boolean);
      for (const cand of candidates) {
        const arr = Array.isArray(cand) ? cand : [];
        if (arr.length > 0) {
          return arr.map((it: any) => {
            const p = it?.product || {};
            const qty = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
            let barcode = '';
            try {
              const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
              if (sc.length > 0) {
                const codes = sc.map((c:any) => String(c?.code || '')).filter(Boolean);
                if (codes.length > 0) barcode = codes.join('/');
              }
            } catch {}
            if (!barcode) {
              barcode = String(
                p?.sorting_barcode
                || p?.barcode_text
                || p?.system_barcode
                || p?.barcode
                || p?.code
                || p?.sku
                || it?.product_code
                || it?.system_code
                || it?.product_id
                || ''
              );
            }
            const productName = String(
              p?.name
              || p?.product_name
              || it?.product_name
              || it?.name
              || '',
            ).trim();
            const customCode = String(
              p?.custom_code
              || p?.product_custom_code
              || p?.sku
              || it?.product_custom_code
              || it?.product_code
              || it?.custom_code
              || '',
            ).trim();
            return { barcode, qty, productName, customCode };
          }).filter(x => x.barcode || x.qty);
        }
      }
    } catch {}
    return [];
  }, []);
  const iuRenderSortingGroupReadable = useCallback((row: any) => {
    try {
      const targetType = String(row?.target_type || '').trim();
      if (targetType && targetType !== 'inventory_unit') return '-';
      const snap = row?.snapshot || {};
      const info = row?.sorting_group_info || snap?.sorting_group_info || {};
      const uid = row?.inventory_unit_id ?? snap?.inventory_unit_id;
      const cached = uid != null ? iuUnitSortingGroupCache[String(uid)] : undefined;
      const code = String(
        info?.system_code
        || info?.code
        || row?.sorting_group_code
        || cached?.code
        || '',
      ).trim();
      const name = String(
        info?.name
        || row?.sorting_group_name
        || cached?.name
        || '',
      ).trim();
      if (code && name) return `${code} (${name})`;
      if (code) return code;
      if (name) return name;
      return '-';
    } catch {
      return '-';
    }
  }, [iuUnitSortingGroupCache]);
  const iuRenderUnitContentItems = useCallback((row: any) => {
    const items = iuExtractUnitContentItems(row);
    if ((!items || items.length === 0) && row?.inventory_unit_id != null) {
      const key = String(row.inventory_unit_id);
      const cached = iuUnitContentCache[key];
      if (cached && cached.length > 0) {
        return (
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {cached.map((it, idx) => (
              <span key={idx} style={{ marginRight: 6 }}>
                {`${it.productName || '-'}(${it.customCode || '-'} ${it.barcode || '-'}) × ${it.qty || 0}`}{idx < cached.length - 1 ? '、' : ''}
              </span>
            ))}
          </div>
        );
      }
    }
    if (!items || items.length === 0) return <span style={{ color: '#999' }}>-</span>;
    return (
      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {items.map((it, idx) => (
          <span key={idx} style={{ marginRight: 6 }}>
            {`${it.productName || '-'}(${it.customCode || '-'} ${it.barcode || '-'}) × ${it.qty || 0}`}{idx < items.length - 1 ? '、' : ''}
          </span>
        ))}
      </div>
    );
  }, [iuExtractUnitContentItems, iuUnitContentCache]);
  useEffect(() => {
    (async () => {
      try {
        if (!targetsOf) return;
        const pendingIds = new Set<string>();
        iuTargetsRows.forEach((r: any) => {
          const has = iuExtractUnitContentItems(r);
          const uid = r?.inventory_unit_id;
          const hasSortingGroup = (() => {
            try {
              const snap = r?.snapshot || {};
              const info = r?.sorting_group_info || snap?.sorting_group_info || {};
              return !!String(
                info?.system_code
                || info?.code
                || info?.name
                || r?.sorting_group_code
                || r?.sorting_group_name
                || '',
              ).trim();
            } catch {
              return false;
            }
          })();
          if (uid != null && ((!has || has.length === 0) || !hasSortingGroup)) {
            const key = String(uid);
            if (!iuUnitContentCache[key] || !iuUnitSortingGroupCache[key]) pendingIds.add(key);
          }
        });
        if (pendingIds.size === 0) return;
        const updates: Record<string, Array<{ barcode: string; qty: number; productName?: string; customCode?: string }>> = {};
        const typeUpdates: Record<string, string> = {};
        const sortingGroupUpdates: Record<string, { code: string; name: string }> = {};
        await Promise.all(Array.from(pendingIds).map(async (id) => {
          try {
            const resp = await getInventoryUnitDetail(id);
            const d: any = (resp as any)?.data || resp;
            const arr = Array.isArray(d?.content?.items) ? d.content.items : (Array.isArray(d?.content_items) ? d.content_items : []);
            if (d?.unit_type) {
              typeUpdates[id] = String(d.unit_type);
            }
            try {
              const sg = d?.sorting_group_info || {};
              const code = String(sg?.system_code || sg?.code || '').trim();
              const name = String(sg?.name || '').trim();
              if (code || name) sortingGroupUpdates[id] = { code, name };
            } catch {}
            const mapped = (arr || []).map((it: any) => {
              const p = it?.product || {};
              const qty = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
              let barcode = '';
              try {
                const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
                if (sc.length > 0) {
                  const codes = sc.map((c:any) => String(c?.code || '')).filter(Boolean);
                  if (codes.length > 0) barcode = codes.join('/');
                }
              } catch {}
              if (!barcode) {
                barcode = String(
                  p?.sorting_barcode
                  || p?.barcode_text
                  || p?.system_barcode
                  || p?.barcode
                  || p?.code
                  || p?.sku
                  || it?.product_system_code
                  || it?.system_code
                  || it?.product_code
                  || ''
                );
              }
              const productName = String(
                p?.name
                || p?.product_name
                || it?.product_name
                || it?.name
                || '',
              ).trim();
              const customCode = String(
                p?.custom_code
                || p?.product_custom_code
                || p?.sku
                || it?.product_custom_code
                || it?.product_code
                || it?.custom_code
                || '',
              ).trim();
              return { barcode, qty, productName, customCode };
            }).filter((x: any) => x.barcode || x.qty);
            if (mapped && mapped.length > 0) updates[id] = mapped;
          } catch {}
        }));
        if (Object.keys(updates).length > 0) {
          setIuUnitContentCache(prev => ({ ...prev, ...updates }));
        }
        if (Object.keys(typeUpdates).length > 0) {
          setIuUnitTypeCache(prev => ({ ...prev, ...typeUpdates }));
        }
        if (Object.keys(sortingGroupUpdates).length > 0) {
          setIuUnitSortingGroupCache(prev => ({ ...prev, ...sortingGroupUpdates }));
        }
      } catch {}
    })();
  }, [targetsOf, iuTargetsRows, iuExtractUnitContentItems, iuUnitContentCache, iuUnitSortingGroupCache]);
  const iuTargetsDataSource = useMemo(() => {
    const arr = Array.isArray(iuTargetsRows) ? iuTargetsRows.slice() : [];
    const noAll = arr.filter((r:any) => !r?.use_all_available);
    const yesAll = arr.filter((r:any) => !!r?.use_all_available);
    const ordered = [...noAll, ...yesAll];
    const start = (iuTargetsPage - 1) * iuTargetsPageSize;
    const end = start + iuTargetsPageSize;
    return ordered.slice(start, end);
  }, [iuTargetsRows, iuTargetsPage, iuTargetsPageSize]);

  // 基础选项加载
  useEffect(() => {
    (async () => {
      try {
        setLoadingProjects(true);
        const ps = await getCurrentUserProjects();
        setProjects(Array.isArray(ps) ? ps : []);
      } catch { setProjects([]); } finally { setLoadingProjects(false); }
    })();
    (async () => {
      try {
        setLoadingWarehouses(true);
        const ws = await getAllWarehouses();
        setWarehouses(Array.isArray(ws) ? ws : []);
      } catch { setWarehouses([]); } finally { setLoadingWarehouses(false); }
    })();
  }, []);

  // 实时搜索（防抖）
  const debounceRef = useRef<any>(null);
  const scheduleReload = () => {
    try { if (debounceRef.current) clearTimeout(debounceRef.current); } catch {}
    debounceRef.current = setTimeout(() => {
      if (mode === 'instruction') {
        loadInstruction().finally(() => refreshCountsForMode('instruction'));
      } else if (mode === 'internal') {
        loadInternal().finally(() => refreshCountsForMode('internal'));
      } else {
        loadBatch().finally(() => refreshCountsForMode('batch'));
      }
    }, 300);
    // 筛选变化重置到第1页
    try { setPager(prev => ({ ...prev, current: 1 })); } catch {}
  };

  // 加载指令模板（系统级优先，其次客户/项目级，中间分割线）
  const loadTemplateOptions = async () => {
    if (tplOptions.length > 0) return;
    setTplLoading(true);
    try {
      const reqs = [
        request.get('/api/v1/instruction/templates/', { params: { level: 'system', page_size: 1000 } }),
        request.get('/api/v1/instruction/templates/', { params: { level: 'customer', page_size: 1000 } }),
        request.get('/api/v1/instruction/templates/', { params: { level: 'project', page_size: 1000 } }),
      ];
      const res = await Promise.allSettled(reqs);
      const all: any[] = [];
      res.forEach((r: any) => {
        if (r.status === 'fulfilled') {
          const data = r.value?.data;
          const rows = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
          all.push(...rows);
        }
      });
      const sys = all.filter((t:any) => String(t.level) === 'system');
      const scoped = all.filter((t:any) => String(t.level) === 'customer' || String(t.level) === 'project');
      const toOpt = (t:any) => {
        const name = String(t.name || '-');
        const code = String(t.system_code || '');
        const full = `${name} · ${code}`.trim();
        return {
          value: code,
          code,
          name,
          level: t.level,
          label: (<span title={full}>{name} <Tag color="blue" style={{ marginLeft: 8 }}>{code}</Tag></span>)
        };
      };
      const sysOpts = sys.map(toOpt);
      const scopedOpts = scoped.map(toOpt);
      const opts: any[] = [...sysOpts];
      if (sysOpts.length > 0 && scopedOpts.length > 0) {
        opts.push({ value: '__DIVIDER__', label: '—— 客户/项目专属 ——', disabled: true });
      }
      opts.push(...scopedOpts);
      setTplOptions(opts);
    } catch {
      setTplOptions([]);
    } finally {
      setTplLoading(false);
    }
  };

  // 客户指令列
  const instrColumns = useMemo(() => {
    const getWarehouseText = (r: any) => {
      const wd = r?.warehouse_detail || {};
      const name = wd?.name || '';
      const code = wd?.code || '';
      if (name || code) return `${name}${code ? ` · ${code}` : ''}`.trim() || '-';
      return r?.warehouse_id || '-';
    };
    // 指令模板（两行：名称 + 代码Tag）
    const renderTemplateReadable = (r: any) => {
      try {
        const snap = r?.template_snapshot || {};
        const detail = r?.template_detail || {};
        const name = snap.name || detail.name || r?.template_name || '-';
        const code = snap.system_code || detail.system_code || r?.template_system_code || r?.template || '';
        return (
          <Space direction="vertical" size={0}>
            <span>{name || '-'}</span>
            {code ? <Tag color="blue">{code}</Tag> : <span style={{ color: '#999' }}>-</span>}
          </Space>
        );
      } catch {
        return <span style={{ color: '#999' }}>-</span>;
      }
    };
    // 项目（两行：名称 + 代码Tag），优先 projects_detail 的第一个
    const renderProjectReadable = (r: any) => {
      try {
        const arr = Array.isArray(r?.projects_detail) ? r.projects_detail : [];
        if (arr.length > 0) {
          const first = arr[0];
          return (
            <Space direction="vertical" size={0}>
              <span>{first?.name || '-'}</span>
              <Tag color="blue">{first?.project_code || first?.code || first?.id}</Tag>
            </Space>
          );
        }
        const name = r?.project_name || r?.project?.name;
        const code = r?.project_code || r?.project?.project_code || r?.project_id;
        if (name || code) {
          return (
            <Space direction="vertical" size={0}>
              <span>{name || '-'}</span>
              <Tag color="blue">{code || '-'}</Tag>
            </Space>
          );
        }
      } catch {}
      return <span style={{ color: '#999' }}>-</span>;
    };
    const renderOpsCountButton = (
      count: number,
      kind: 'IU' | 'IOC',
      onClick: () => void,
    ) => {
      const badgeStyle = kind === 'IU'
        ? { background: '#f6ffed', border: '1px solid #b7eb8f', color: '#389e0d' }
        : { background: '#fff7e6', border: '1px solid #ffd591', color: '#d46b08' };
      return (
        <Tooltip title={kind === 'IU' ? 'IU：当前可用库存的库存单元数量' : 'IOC：指令产出容器数量'}>
          <Button type="link" size="small" onClick={onClick}>
            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
              <span>{Number(count || 0)}</span>
              <span
                style={{
                  ...badgeStyle,
                  fontSize: 12,
                  lineHeight: 1.2,
                  borderRadius: 10,
                  padding: '1px 6px',
                  fontWeight: 600,
                }}
              >
                {kind}
              </span>
            </span>
          </Button>
        </Tooltip>
      );
    };
    // 预估操作数量：targets_detail.expected_qty 之和；多种字段兜底
    const renderEstimatedOps = (r: any) => {
      try {
        const links = Array.isArray(r?.targets_detail) ? r.targets_detail : [];
        const hasHistoryIoc = (links || []).some((t:any) => String(t?.target_type || '').trim() === 'instruction_output');
        if (hasHistoryIoc) {
          const containerCount = countHistoryIocContainersFromRecord(r);
          if (containerCount > 0) {
            return renderOpsCountButton(containerCount, 'IOC', () => {
              setHistoryTargetsPage(1);
              setHistoryTargetsPageSize(10);
              setHistoryTargetsOf(r);
            });
          }
          return '-';
        }
        if (links.length > 0) {
          let total = 0;
          for (const t of links) {
            const v = Number((t && t.expected_qty) ?? 0);
            if (!isNaN(v)) total += v;
          }
          if (total > 0) {
            return renderOpsCountButton(total, 'IU', () => setTargetsOf(r));
          }
        }
      } catch {}
      if (r.estimated_operation_count != null) return r.estimated_operation_count;
      if (r.expected_operation_count != null) return r.expected_operation_count;
      if (Array.isArray(r?.actions_detail)) return r.actions_detail.length;
      const snap = r?.template_snapshot;
      const cnt = Array.isArray(snap?.actions) ? snap.actions.length : null;
      return cnt != null ? cnt : '-';
    };
    const renderPriority = (v: string) => {
      const label = v === 'super' ? '超级优先' : v === 'high' ? '一般优先' : '普通';
      const color = v === 'super' ? 'red' : v === 'high' ? 'orange' : 'default';
      return <Tag color={color}>{label}</Tag>;
    };
    const isOverdue = (r: any) => {
      try {
        const rv = r?.reviewed_at;
        if (!rv) return false;
        const t = new Date(rv as any).getTime();
        if (isNaN(t)) return false;
        const now = Date.now();
        const days = (now - t) / (24 * 60 * 60 * 1000);
        return days > 5;
      } catch { return false; }
    };
    const timeCell = (label: string, v: any) => (v ? <TimezoneAwareTime time={v} format="YYYY-MM-DD HH:mm" showTimezone={false} /> : <span style={{ color: '#999' }}>-</span>);
    return [
      { title: '指令系统编码', dataIndex: 'system_code', width: 200, render: (v:any) => (
        v ? <Typography.Text copyable={{ text: String(v) }}>{v}</Typography.Text> : <span style={{ color: '#999' }}>-</span>
      ) },
      { title: '指令名称', dataIndex: 'name', width: 260, render: (v:any, r:any) => (
        <Space size={6}>
          <span>{v || '-'}</span>
          {isOverdue(r) && (
            <span style={{ color: '#d48806', display: 'inline-flex', alignItems: 'center' }}>
              <ExclamationCircleOutlined style={{ marginRight: 4 }} />超期未执行
            </span>
          )}
        </Space>
      ) },
      { title: '指令模板', key: 'template', width: 240, render: (_:any, r:any) => renderTemplateReadable(r) },
      { title: '项目', key: 'project', width: 200, render: (_:any, r:any)=> renderProjectReadable(r) },
      { title: '预估操作数量', key: 'op_count', width: 140, render: (_:any, r:any)=> renderEstimatedOps(r) },
      { title: '优先级', dataIndex: 'priority', width: 100, render: (v:string)=> renderPriority(String(v || 'normal')) },
      { title: '预计耗时(天)', dataIndex: 'estimated_working_days', width: 130, render: (v:any)=> (v ?? '-') },
      { title: '仓库', key: 'warehouse', width: 260, render: (_:any, r:any)=> getWarehouseText(r) },
      { title: '指令创建/初审时间', key: 'times_review', width: 280, render: (_:any, r:any) => (
        <div>
          <div>指令创建：{timeCell('创建', r?.created_at)}</div>
          <div>指令初审：{timeCell('审核', r?.reviewed_at)}</div>
        </div>
      )},
      { title: '指令执行/终审提交时间', key: 'times_exec', width: 280, render: (_:any, r:any) => {
        // 开始/完成时间：读取后端标准字段，旧数据兜底 meta
        const started = r?.execution_started_at || r?.meta?.execution_started_at;
        const finished = r?.completed_at || r?.meta?.execution_completed_at;
        return (
          <div>
            <div>开始执行：{timeCell('开始', started)}</div>
            <div>终审提交：{timeCell('完成', finished)}</div>
          </div>
        );
      }},
      { title: '操作', key: 'ops', width: 60, render: (_:any, r:any) => {
      const id = r.id;
      const isAdminPath = location.pathname.startsWith('/dashboard/admin');
      const toExec = () => navigate(isAdminPath ? `/dashboard/admin/execution/instruction/${id}` : `/dashboard/staff/execution/instruction/${id}`);
      const toView = () => {
        // 统一使用通用详情路由，避免 /dashboard/staff 前缀未注册导致404
        navigate(`/dashboard/instructions/${id}`);
      };
      const claim = async () => {
        try {
          // 领取前尝试“计费计划预览”（软校验）：ok=false 且 code=baseline_not_ready 时不阻断，仅提示
          try {
            const pv = await request.post(`/api/v1/instruction/instructions/${id}/plan/preview/`, {});
            const ok = !!pv?.data?.ok;
            const code = pv?.data?.code;
            if (!ok && code === 'baseline_not_ready') {
              message.warning('基准数据未就绪，计费预览不可用，不影响执行');
            }
          } catch { /* 预览异常不阻断领取 */ }
          // 优先尝试基于“执行任务”的领取（将任务置为 in_progress），以免仍然留在“待领取”列表
          const taskId = Number((r as any)?._pending_task_id);
          if (Number.isFinite(taskId)) {
            try {
              await request.post(`/api/v1/instruction/exec/tasks/${taskId}/accept/`, {});
            } catch { /* 后端未提供 accept 接口时忽略 */ }
          }
          // 兼容：同时调用指令层面的领取，确保状态进入“执行中”
          await request.post(`/api/v1/instruction/instructions/${id}/claim/`, {});
          message.success('已领取整单');
          // 领取成功后：切换到“执行中”页签，由 useEffect 自动加载“我在执行”数据
          try { setTab('mine'); } catch {}
          // 不在此处手动调用 loadInstruction()，避免闭包里的旧 tab（todo）回写覆盖当前列表
        } catch (e:any) {
          message.error(e?.response?.data?.detail || '领取失败');
        }
      };
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Button size="small" onClick={toView}>查看</Button>
          {tab === 'todo'
            ? <Button size="small" type="primary" onClick={claim}>领取整单</Button>
            : <Button size="small" type="primary" onClick={toExec}>{tab === 'done' ? '查看执行结果' : '继续执行'}</Button>}
        </div>
      );
      } },
    ];
  }, [tab, navigate, location.pathname, countHistoryIocContainersFromRecord]);

  // 仓内工单列
  const internalColumns = useMemo(() => {
    const statusColor = (s: string) => (
      s === 'completed' ? 'green'
        : s === 'in_progress' ? 'blue'
        : s === 'pending' ? 'gold'
        : s === 'accepted' ? 'cyan'
        : s === 'rejected' ? 'red'
        : s === 'cancelled' ? 'red'
        : 'default'
    );
    const statusText = (s: string) => ({
      pending: '待领取', accepted: '已领取', in_progress: '执行中', completed: '已完成', rejected: '已驳回', cancelled: '已取消'
    } as any)[s] || s || '-';
    return [
      { title: '系统编码', dataIndex: 'system_code', width: 180 },
      { title: '类型', dataIndex: 'type_code', width: 140 },
      { title: '优先级', dataIndex: 'priority', width: 100, render: (v:string)=> <Tag color={v==='super'?'red':(v==='high'?'orange':'default')}>{v || 'normal'}</Tag> },
      { title: '状态', dataIndex: 'status', width: 120, render: (v:any)=> <Tag color={statusColor(String(v))}>{statusText(String(v))}</Tag> },
      { title: '操作', key: 'ops', width: 100, render: (_:any, r:any) => {
        const isAdminPath = location.pathname.startsWith('/dashboard/admin');
        const base = isAdminPath ? '/dashboard/admin' : '/dashboard/staff';
        const on = async (kind: 'accept'|'start'|'complete') => {
          try {
            if (kind==='accept') await request.post(`/api/v1/workorders/workorders/${r.id}/accept/`, {});
            if (kind==='start') await request.post(`/api/v1/workorders/workorders/${r.id}/start/`, {});
            if (kind==='complete') await request.post(`/api/v1/workorders/workorders/${r.id}/complete/`, {});
            message.success('已提交');
            loadInternal();
          } catch (e:any) {
            message.error(e?.response?.data?.detail || '操作失败');
          }
        };
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {/* 工单详情页已移除：避免死链 */}
            {r.status === 'pending' && <Button size="small" onClick={() => on('accept')}>领取</Button>}
            {r.status === 'accepted' && <Button size="small" onClick={() => on('start')}>开始</Button>}
            {r.status === 'in_progress' && <Button size="small" onClick={() => on('complete')}>完成</Button>}
          </div>
        );
      } },
    ] as any[];
  }, [navigate, location.pathname]);

  // 批次执行列（仅一件代发）
  const formatKindQty = useCallback((raw: any) => {
    const s = String(raw || '').trim();
    if (!s) return [];
    const [k, q] = s.split('|').map((v) => v.trim());
    return [
      { label: '种类', val: k || '0' },
      { label: '数量', val: q || '0' },
    ];
  }, []);
  const formatWaybillSummary = useCallback((raw: any) => {
    const data = raw && typeof raw === 'object' ? raw : null;
    if (!data) return [];
    const rows = [
      { label: '有运单', val: String((data as any).upload_immediate || '').trim() },
      { label: '仓库提供运单', val: String((data as any).warehouse || '').trim() },
      { label: '先申请包装数据', val: String((data as any).upload_after_pack || '').trim() },
    ];
    return rows.filter((r) => {
      const m = String(r.val || '').match(/^(\d+)\s*单/);
      const orders = m ? Number(m[1]) : 0;
      return orders > 0;
    });
  }, []);
  const formatPackSummary = useCallback((raw: any) => {
    const s = String(raw || '').trim();
    if (!s) return [];
    const [a, b] = s.split('|').map((v) => v.trim());
    return [
      { label: '无需打包', val: a || '-' },
      { label: '需打包', val: b || '-' },
    ];
  }, []);
  const formatSkuMixSummary = useCallback((raw: any) => {
    const s = String(raw || '').trim();
    if (!s) return [];
    const parts = s.split('|').map((v) => v.trim());
    const labels = ['单款单件', '单款多件', '多款多件'];
    return parts
      .map((val, idx) => ({ label: labels[idx] || '', val }))
      .filter((item) => item.val && item.val !== '-' && item.label);
  }, []);

  const batchColumns = useMemo(() => {
    const statusColor = (s: string) => (
      s === 'executed' ? 'green'
        : s === 'executing' ? 'blue'
        : s === 'precheck_done' ? 'gold'
        : s === 'cancelled' ? 'red'
        : 'default'
    );
    const statusText = (s: string) => ({
      precheck_done: '待领取',
      executing: '执行中',
      executed: '操作阶段已完成',
      cancelled: '已取消',
      draft: '草稿'
    } as any)[s] || s || '-';
    return [
      {
        title: '批次编码',
        dataIndex: 'system_code',
        key: 'system_code',
        width: 140,
        render: (v: any) => (v ? <Typography.Text code copyable={{ text: String(v) }}>{v}</Typography.Text> : '-'),
      },
      {
        title: '指令数量',
        key: 'items',
        width: 100,
        render: (_: any, r: any) => (Array.isArray(r?.items) ? r.items.length : 0),
      },
      {
        title: '库存单元',
        key: 'inventory_unit_summary',
        width: 140,
        render: (_: any, r: any) => {
          const rows = formatKindQty(r?.inventory_unit_summary);
          if (!rows.length) return '-';
          const detail = Array.isArray(r?.inventory_unit_detail) ? r.inventory_unit_detail : [];
          const body = (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {rows.map((item) => (
                <div key={item.label}>{`${item.label}-${item.val}${item.label === '种类' ? '种' : '件'}`}</div>
              ))}
            </div>
          );
          if (!detail.length) return body;
          return (
            <Button
              type="link"
              style={{ padding: 0, height: 'auto', textAlign: 'left' }}
              onClick={() => {
                setInventoryModalRows(detail);
                setInventoryModalTitle(`库存单元汇总${r?.system_code ? ` - ${r.system_code}` : ''}`);
                setInventoryModalOpen(true);
              }}
            >
              {body}
            </Button>
          );
        },
      },
      {
        title: '承运商分布',
        key: 'carrier_kind_count',
        width: 160,
        render: (_: any, r: any) => {
          const detail = Array.isArray(r?.carrier_kind_detail) ? r.carrier_kind_detail : [];
          if (detail.length === 0) return '-';
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {detail.map((item: any, idx: number) => (
                <div key={`${item?.carrier || '-'}-${idx}`}>
                  {`${item?.carrier || '-'}-${Number(item?.count || 0)}单${Number(item?.qty || 0)}件`}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        title: '指令类型分布',
        key: 'sku_mix',
        width: 180,
        render: (_: any, r: any) => {
          const rows = formatSkuMixSummary(r?.sku_mix_summary);
          if (!rows.length) return '-';
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {rows.map((item) => (
                <div key={item.label}>{`${item.label}-${item.val}`}</div>
              ))}
            </div>
          );
        },
      },
      {
        title: '是否需打包',
        key: 'packing_required',
        width: 160,
        render: (_: any, r: any) => {
          const rows = formatPackSummary(r?.pack_required_summary);
          if (!rows.length) return '-';
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {rows.map((item) => (
                <div key={item.label}>{`${item.label}-${item.val}`}</div>
              ))}
            </div>
          );
        },
      },
      {
        title: '运单状态分布',
        key: 'waybill_mode',
        width: 180,
        render: (_: any, r: any) => {
          const rows = formatWaybillSummary(r?.waybill_mode_detail);
          if (!rows.length) return '-';
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {rows.map((item) => (
                <div key={item.label}>{`${item.label}-${item.val}`}</div>
              ))}
            </div>
          );
        },
      },
      {
        title: '创建时间',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 130,
        render: (v: any) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-',
      },
      {
        title: '操作',
        key: 'actions',
        fixed: 'right' as const,
        width: 140,
        render: (_: any, r: any) => {
        const isAdminPath = location.pathname.startsWith('/dashboard/admin');
        const claim = async () => {
          try {
            await request.post(`/api/v1/instruction/batches/${r?.id}/exec/claim/`, {});
            message.success('已领取整单');
            try { setTab('mine'); } catch {}
            try { await loadBatch(); } catch {}
          } catch (e:any) {
            message.error(e?.response?.data?.detail || '领取失败');
          }
        };
        const goExec = async () => {
          const base = isAdminPath ? '/dashboard/admin' : '/dashboard/staff';
          navigate(`${base}/execution/batch/${r?.id}`);
        };
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Button
              size="small"
              onClick={() => {
                try {
                  const path = String((window as any)?.location?.pathname || '');
                  const base = path.startsWith('/dashboard/staff')
                    ? '/dashboard/staff'
                    : path.startsWith('/dashboard/admin') ? '/dashboard/admin' : '/dashboard/customer';
                  navigate(`${base}/instructions/batch/${r?.id}`);
                } catch {}
              }}
            >
              查看
            </Button>
            {tab === 'todo'
              ? <Button size="small" type="primary" onClick={claim}>领取整单</Button>
              : (
                <Button size="small" type="primary" onClick={goExec}>
                  {String(r?.status || '').toLowerCase() === 'executed' ? '查看执行结果' : '继续执行'}
                </Button>
              )}
          </div>
        );
      } },
    ] as any[];
  }, [tab, navigate, location.pathname, formatKindQty, formatSkuMixSummary, formatPackSummary, formatWaybillSummary]);

  const modeMeta = useMemo(() => {
    const m: Record<string, { label: string; color: string; bg: string; border: string }> = {
      batch: { label: '客户指令-批次执行(1件代发)', color: '#fa8c16', bg: '#fff7e6', border: '#ffd591' },
      instruction: { label: '客户指令-单指令执行', color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff' },
      internal: { label: '仓内工单', color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f' },
    };
    return m[mode] || m.batch;
  }, [mode]);

  const subTabLabelMap = useMemo(() => ({
    todo: t('execution.todo'),
    mine: t('execution.running'),
    done: '操作阶段已完成',
  }), [t]);

  const items: TabsProps['items'] = useMemo(() => ([
    { key: 'todo', label: (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '4px 10px',
          borderRadius: 6,
          border: tab === 'todo' ? `1px solid ${modeMeta.border}` : '1px solid #d9d9d9',
          background: tab === 'todo' ? modeMeta.bg : '#ffffff',
          color: tab === 'todo' ? modeMeta.color : '#595959',
          fontWeight: tab === 'todo' ? 700 : 500,
        }}
      >
        <span>{subTabLabelMap.todo}</span>
        <Badge
          count={countByModeTab[`${mode}:todo`] ?? 0}
          showZero
          style={{ backgroundColor: tab === 'todo' ? modeMeta.color : '#bfbfbf' }}
        />
      </span>
    ) },
    { key: 'mine', label: (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '4px 10px',
          borderRadius: 6,
          border: tab === 'mine' ? `1px solid ${modeMeta.border}` : '1px solid #d9d9d9',
          background: tab === 'mine' ? modeMeta.bg : '#ffffff',
          color: tab === 'mine' ? modeMeta.color : '#595959',
          fontWeight: tab === 'mine' ? 700 : 500,
        }}
      >
        <span>{subTabLabelMap.mine}</span>
        <Badge
          count={countByModeTab[`${mode}:mine`] ?? 0}
          showZero
          style={{ backgroundColor: tab === 'mine' ? modeMeta.color : '#bfbfbf' }}
        />
      </span>
    ) },
    { key: 'done', label: (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '4px 10px',
          borderRadius: 6,
          border: tab === 'done' ? `1px solid ${modeMeta.border}` : '1px solid #d9d9d9',
          background: tab === 'done' ? modeMeta.bg : '#ffffff',
          color: tab === 'done' ? modeMeta.color : '#595959',
          fontWeight: tab === 'done' ? 700 : 500,
        }}
      >
        <span>{subTabLabelMap.done}</span>
        <Badge
          count={countByModeTab[`${mode}:done`] ?? 0}
          showZero
          style={{ backgroundColor: tab === 'done' ? modeMeta.color : '#bfbfbf' }}
        />
      </span>
    ) },
  ]), [mode, modeMeta, tab, subTabLabelMap, countByModeTab]);

  // 当前页数据（基于本地分页）
  const pageData = useMemo(() => {
    try {
      const start = (pager.current - 1) * pager.pageSize;
      const end = start + pager.pageSize;
      return rows.slice(start, end);
    } catch {
      return rows;
    }
  }, [rows, pager.current, pager.pageSize]);

  return (
    <div style={{ padding: 12 }}>
      <Card>
        <style>{`
          .todo-main-segmented.ant-segmented .ant-segmented-thumb {
            display: none !important;
          }
          .todo-main-segmented.ant-segmented .ant-segmented-item,
          .todo-main-segmented.ant-segmented .ant-segmented-item:hover,
          .todo-main-segmented.ant-segmented .ant-segmented-item-selected {
            background: transparent !important;
            box-shadow: none !important;
          }
          .todo-main-segmented.ant-segmented .ant-segmented-item-label {
            padding: 0 !important;
          }
          .todo-main-segmented.ant-segmented .ant-segmented-item {
            margin-right: 8px !important;
          }
          .todo-main-segmented.ant-segmented .ant-segmented-item:last-child {
            margin-right: 0 !important;
          }
          .todo-sub-tabs .ant-tabs-ink-bar {
            display: none !important;
          }
          .todo-sub-tabs .ant-tabs-nav::before {
            border-bottom: none !important;
          }
          .todo-sub-tabs .ant-tabs-tab {
            margin: 0 !important;
            padding: 0 !important;
          }
          .todo-sub-tabs .ant-tabs-tab + .ant-tabs-tab {
            margin-left: 8px !important;
          }
        `}</style>
        {(() => {
          const optStyle = (active: boolean, kind: 'instruction'|'internal'|'batch'): React.CSSProperties => {
            const base = {
              padding: '6px 10px',
              borderRadius: 6,
              fontWeight: active ? 700 : 500,
              border: '1px solid #d9d9d9',
              background: '#ffffff',
              color: '#595959',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            } as React.CSSProperties;
            if (!active) return base;
            if (kind === 'instruction') {
              return { ...base, color: '#1890ff', border: '1px solid #91d5ff', background: '#e6f7ff' };
            }
            if (kind === 'internal') {
              return { ...base, color: '#52c41a', border: '1px solid #b7eb8f', background: '#f6ffed' };
            }
            return { ...base, color: '#fa8c16', border: '1px solid #ffd591', background: '#fff7e6' };
          };
          const modeCount = (kind: 'instruction'|'internal'|'batch') => {
            const a = Number(countByModeTab[`${kind}:todo`] ?? 0);
            const b = Number(countByModeTab[`${kind}:mine`] ?? 0);
            const c = Number(countByModeTab[`${kind}:done`] ?? 0);
            return a + b + c;
          };
          const segOptions = [
            {
              value: 'batch',
              label: (
                <span style={optStyle(mode==='batch','batch') as any}>
                  <span>客户指令-批次执行(1件代发)</span>
                  <Badge count={modeCount('batch')} showZero style={{ backgroundColor: mode==='batch' ? '#fa8c16' : '#bfbfbf' }} />
                </span>
              )
            },
            {
              value: 'instruction',
              label: (
                <span style={optStyle(mode==='instruction','instruction') as any}>
                  <span>客户指令-单指令执行</span>
                  <Badge count={modeCount('instruction')} showZero style={{ backgroundColor: mode==='instruction' ? '#1890ff' : '#bfbfbf' }} />
                </span>
              )
            },
            {
              value: 'internal',
              label: (
                <span style={optStyle(mode==='internal','internal') as any}>
                  <span>仓内工单</span>
                  <Badge count={modeCount('internal')} showZero style={{ backgroundColor: mode==='internal' ? '#52c41a' : '#bfbfbf' }} />
                </span>
              )
            },
          ];
          return (
            <div style={{ marginBottom: 8 }}>
              <Segmented
                className="todo-main-segmented"
                value={mode}
                onChange={handleModeChange}
                options={segOptions as any}
                style={{ background: 'transparent', border: 'none', boxShadow: 'none', padding: 0 }}
              />
            </div>
          );
        })()}
        {/* 指令模式筛选区域 */}
        {mode === 'instruction' && (
          <Form form={form} layout="horizontal" onFinish={() => loadInstruction()} onValuesChange={scheduleReload} style={{ marginBottom: 8 }}>
            <Row gutter={[12, 8]}>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="system_code" label="系统编码">
                  <Input placeholder="指令系统编码" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="keyword" label="名称/备注">
                  <Input placeholder="名称或备注" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="project_id" label="项目">
                  <Select placeholder="选择项目" allowClear loading={loadingProjects} showSearch optionFilterProp="children">
                    {projects.map((p:any) => (
                      <Select.Option key={p.id} value={String(p.id)}>
                        {p.project_code ? `${p.project_code} - ${p.name}` : (p.name || p.id)}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="warehouse_id" label="仓库">
                  <Select
                    placeholder="选择仓库"
                    allowClear={!isWarehouseScopedUser}
                    disabled={isWarehouseScopedUser && warehouseOptionsForExecution.length <= 1}
                    loading={loadingWarehouses}
                    showSearch
                    optionFilterProp="children"
                  >
                    {warehouseOptionsForExecution.map((w:any) => (
                      <Select.Option key={w.id} value={String(w.id)}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ marginRight: 12 }}>{w.name || '-'}</span>
                          <span style={{ color: '#8c8c8c' }}>{w.code || w.warehouse_code || w.id}</span>
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[12, 8]}>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="priority" label="优先级">
                  <Select placeholder="选择优先级" allowClear style={{ width: 180 }}>
                    <Select.Option value="super">超级优先</Select.Option>
                    <Select.Option value="high">一般优先</Select.Option>
                    <Select.Option value="normal">普通</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={10}>
                <Form.Item name="template_code" label="指令模板">
                  <Select
                    placeholder="选择指令模板"
                    allowClear
                    loading={tplLoading}
                    showSearch
                    optionFilterProp="label"
                    onFocus={() => loadTemplateOptions()}
                    options={tplOptions as any}
                    style={{ minWidth: 260 }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item>
                  <Space>
                    <Button type="primary" onClick={() => form.submit()}>搜索</Button>
                    <Button onClick={() => { form.resetFields(); loadInstruction(); }}>重置</Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}
        {mode === 'batch' && (
          <Form form={form} layout="horizontal" onFinish={() => loadBatch()} onValuesChange={scheduleReload} style={{ marginBottom: 8 }}>
            <Row gutter={[12, 8]}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="batch_system_code" label="系统编码">
                  <Input placeholder="批次系统编码" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="batch_instruction_system_code" label="所含指令系统编码">
                  <Input placeholder="指令系统编码（支持模糊）" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="batch_warehouse_id" label="仓库">
                  <Select
                    placeholder="选择仓库"
                    allowClear={!isWarehouseScopedUser}
                    disabled={isWarehouseScopedUser && warehouseOptionsForExecution.length <= 1}
                    loading={loadingWarehouses}
                    showSearch
                    optionFilterProp="children"
                  >
                    {warehouseOptionsForExecution.map((w:any) => (
                      <Select.Option key={w.id} value={String(w.id)}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ marginRight: 12 }}>{w.name || '-'}</span>
                          <span style={{ color: '#8c8c8c' }}>{w.code || w.warehouse_code || w.id}</span>
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[12, 8]}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item>
                  <Space>
                    <Button type="primary" onClick={() => form.submit()}>搜索</Button>
                    <Button onClick={() => {
                      form.setFieldsValue({
                        batch_system_code: undefined,
                        batch_instruction_system_code: undefined,
                        batch_warehouse_id: undefined,
                      });
                      loadBatch();
                    }}>重置</Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}
        <Tabs
          className="todo-sub-tabs"
          activeKey={tab}
          onChange={handleSubTabChange}
          items={items}
        />
        <div style={{ margin: '8px 0' }}>
          <Tag color="blue">共 {pager.total} 条记录</Tag>
        </div>
        {mode === 'instruction' ? (
          <Table
            rowKey={(r:any)=> String(r.id)}
            loading={loading}
            dataSource={pageData}
            columns={instrColumns as any}
            pagination={{
              current: pager.current,
              pageSize: pager.pageSize,
              total: pager.total,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: [10, 20, 50, 100] as any,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
              onChange: (p, ps) => { try { setPager(prev => ({ ...prev, current: p, pageSize: ps })); } catch {} }
            }}
          />
        ) : mode === 'internal' ? (
          <Table
            rowKey={(r:any)=> String(r.id)}
            loading={loading}
            dataSource={pageData}
            columns={internalColumns as any}
            pagination={{
              current: pager.current,
              pageSize: pager.pageSize,
              total: pager.total,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: [10, 20, 50, 100] as any,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
              onChange: (p, ps) => { try { setPager(prev => ({ ...prev, current: p, pageSize: ps })); } catch {} }
            }}
          />
        ) : (
          <Table
            rowKey={(r:any)=> String(r.id)}
            loading={loading}
            dataSource={pageData}
            columns={batchColumns as any}
            pagination={{
              current: pager.current,
              pageSize: pager.pageSize,
              total: pager.total,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: [10, 20, 50, 100] as any,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
              onChange: (p, ps) => { try { setPager(prev => ({ ...prev, current: p, pageSize: ps })); } catch {} }
            }}
          />
        )}

        <Modal
          open={inventoryModalOpen}
          title={(
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 32 }}>
              <span>{inventoryModalTitle || '库存单元汇总'}</span>
              <Button
                size="small"
                onClick={async () => {
                  try {
                    const header = ['库存单元编码', '分拣条码', '客户自定义编码', '可用库存数量', '发货总数量', '库存是否充足'].join('\t');
                    const lines = (inventoryModalRows || []).map((r: any) => ([
                      r?.inventory_unit_code || '-',
                      r?.sorting_barcodes || '-',
                      r?.custom_codes || '-',
                      Number(r?.available_qty || 0),
                      r?.ship_qty || '0',
                      r?.is_sufficient ? '充足' : '不足',
                    ].join('\t')));
                    const text = [header, ...lines].join('\n');
                    await navigator.clipboard.writeText(text);
                    message.success('已复制');
                  } catch {
                    message.error('复制失败');
                  }
                }}
              >
                复制
              </Button>
            </div>
          )}
          footer={null}
          width={920}
          onCancel={() => setInventoryModalOpen(false)}
        >
          <Table
            rowKey={(r: any) => String(r?.inventory_unit_id || '')}
            dataSource={inventoryModalRows}
            pagination={false}
            columns={[
              { title: '库存单元编码', dataIndex: 'inventory_unit_code', key: 'inventory_unit_code', width: 140, render: (v: any) => v || '-' },
              { title: '分拣条码', dataIndex: 'sorting_barcodes', key: 'sorting_barcodes', width: 160, render: (v: any) => v || '-' },
              { title: '客户自定义编码', dataIndex: 'custom_codes', key: 'custom_codes', width: 160, render: (v: any) => v || '-' },
              { title: '可用库存数量', dataIndex: 'available_qty', key: 'available_qty', width: 120, render: (v: any) => (Number(v) || 0) },
              { title: '发货总数量', dataIndex: 'ship_qty', key: 'ship_qty', width: 120, render: (v: any) => v || '0' },
              {
                title: '库存是否充足',
                dataIndex: 'is_sufficient',
                key: 'is_sufficient',
                width: 120,
                render: (v: any) => (
                  <Tag color={v ? 'green' : 'red'}>{v ? '充足' : '不足'}</Tag>
                ),
              },
            ]}
          />
        </Modal>

        {/* 历史指令输入IOC明细弹窗（与列表页口径对齐） */}
        <Modal
          title={(
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%', paddingRight: 140 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                <span>操作对象明细(历史指令执行结果)</span>
                <span style={{ color: '#d9480f' }}>{String(historyTargetsOf?.system_code || '')}</span>
              </span>
              <span style={{ marginRight: 12 }}>
                {(() => {
                  const rows = Array.isArray(historyTargetsVisualRows) ? historyTargetsVisualRows : [];
                  const total = countUniqueHistoryIocRows(rows);
                  const prjCount = (() => {
                    try {
                      const set = new Set<string>();
                      rows.forEach((r:any) => {
                        const pid = Number(r?.project_id);
                        if (Number.isFinite(pid)) set.add(String(pid));
                        else if (r?.project_code) set.add(`code:${String(r.project_code)}`);
                      });
                      return set.size;
                    } catch { return 0; }
                  })();
                  const totalQty = rows.reduce((acc:number, r:any) => {
                    const n = Number(
                      r?.output_contents_direct_total_qty != null
                        ? r?.output_contents_direct_total_qty
                        : (r?.output_contents_total_qty || 0),
                    );
                    return acc + (Number.isFinite(n) ? n : 0);
                  }, 0);
                  return (
                    <>
                      <span style={{ display:'inline-block', padding:'2px 8px', border:'1px solid #91caff', background:'#e6f4ff', color:'#0958d9', borderRadius:4, fontWeight:600, marginRight:8 }}>
                        涉及项目数量 {prjCount}
                      </span>
                      <span style={{ display:'inline-block', padding:'2px 8px', border:'1px solid #ffe58f', background:'#fffbe6', color:'#ad8b00', borderRadius:4, fontWeight:600, marginRight:8 }}>
                        指令产出容器(IOC)总数量 {total}
                      </span>
                      <span style={{ display:'inline-block', padding:'2px 8px', border:'1px solid #b7eb8f', background:'#f6ffed', color:'#389e0d', borderRadius:4, fontWeight:600 }}>
                        容器内库存单元总数量 {totalQty}
                      </span>
                    </>
                  );
                })()}
              </span>
            </div>
          )}
          open={!!historyTargetsOf}
          onCancel={() => { setHistoryTargetsOf(null); setHistoryTargetsRows([]); }}
          footer={null}
          width={1280}
        >
          <style>{`
            .todo-history-target-ioc-table .ant-table-tbody > tr.todo-history-target-ioc-group-row > td {
              border-left: 2px solid #91caff !important;
              border-right: 2px solid #91caff !important;
            }
            .todo-history-target-ioc-table .ant-table-tbody > tr.todo-history-target-ioc-group-start > td {
              border-top: 2px solid #91caff !important;
            }
            .todo-history-target-ioc-table .ant-table-tbody > tr.todo-history-target-ioc-group-end > td {
              border-bottom: 2px solid #91caff !important;
            }
          `}</style>
          <Table
            className="todo-history-target-ioc-table"
            size="small"
            rowKey={(r:any, i?:number)=> String(r?.__key || r?.output_container_id || r?.output_code || i)}
            dataSource={(() => {
              const arr = Array.isArray(historyTargetsVisualRows) ? historyTargetsVisualRows : [];
              const start = (historyTargetsPage - 1) * historyTargetsPageSize;
              const end = start + historyTargetsPageSize;
              return arr.slice(start, end);
            })()}
            loading={historyTargetsLoading}
            onRow={(record: any, index?: number) => {
              const rowKey = String(record?.__key || record?.output_container_id || record?.output_code || index);
              const borderMeta = historyTargetsVisualMeta.rowGroupBorderMap.get(rowKey) || { grouped: false, top: false, bottom: false };
              return {
                style: {
                  borderLeft: borderMeta.grouped ? '2px solid #91caff' : undefined,
                  borderRight: borderMeta.grouped ? '2px solid #91caff' : undefined,
                  borderTop: borderMeta.top ? '2px solid #91caff' : undefined,
                  borderBottom: borderMeta.bottom ? '2px solid #91caff' : undefined,
                },
              };
            }}
            rowClassName={(record: any, index?: number) => {
              const rowKey = String(record?.__key || record?.output_container_id || record?.output_code || index);
              const borderMeta = historyTargetsVisualMeta.rowGroupBorderMap.get(rowKey) || { grouped: false, top: false, bottom: false };
              if (!borderMeta.grouped) return '';
              if (borderMeta.top) return 'todo-history-target-ioc-group-row todo-history-target-ioc-group-start';
              if (borderMeta.bottom) return 'todo-history-target-ioc-group-row todo-history-target-ioc-group-end';
              return 'todo-history-target-ioc-group-row';
            }}
            pagination={{
              current: historyTargetsPage,
              pageSize: historyTargetsPageSize,
              total: Array.isArray(historyTargetsVisualRows) ? historyTargetsVisualRows.length : 0,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['10','20','50','100'] as any,
              onChange: (p:number, ps:number) => { setHistoryTargetsPage(p); setHistoryTargetsPageSize(ps); },
            }}
            columns={[
              { title: '项目', key: 'project', render: (_:any, row:any) => {
                const code = row?.project_code || row?.project_id;
                const name = row?.project_name || '';
                return code ? `${code}${name ? ` - ${name}` : ''}` : '-';
              }},
              { title: '容器系统编码', key: 'output_code', render: (_:any, row:any) => <span style={{ fontFamily: 'monospace' }}>{String(row?.output_code || row?.display_code || '-')}</span> },
              {
                title: '层级标识',
                key: '__hierarchy_role__',
                width: 120,
                render: (_:any, row:any) => {
                  const rowKey = String(row?.__key || row?.output_container_id || row?.output_code || '');
                  const role = historyTargetsVisualMeta.rowRoleMap.get(rowKey) || 'standalone';
                  const roleText = role === 'parent' ? '父容器' : (role === 'child' ? '子容器' : '独立容器');
                  const roleStyle = role === 'parent'
                    ? { color: '#1677ff', border: '#91caff', bg: '#e6f4ff' }
                    : (role === 'child'
                      ? { color: '#d4380d', border: '#ffbb96', bg: '#fff2e8' }
                      : { color: '#389e0d', border: '#b7eb8f', bg: '#f6ffed' });
                  return (
                    <span
                      style={{
                        display: 'inline-block',
                        minWidth: 76,
                        textAlign: 'center',
                        padding: '2px 10px',
                        borderRadius: 6,
                        border: `1px solid ${roleStyle.border}`,
                        color: roleStyle.color,
                        background: roleStyle.bg,
                        lineHeight: 1.4,
                      }}
                    >
                      {roleText}
                    </span>
                  );
                },
              },
              { title: '容器类型', dataIndex: 'container_type_display', width: 120, render: (v:any)=> {
                const key = String(v || '').trim().toLowerCase();
                return overviewContainerTypeTextMap[key] || String(v || '-');
              }},
              { title: '内容类型', dataIndex: 'output_content_kind_display', width: 140, render: (v:any, row:any)=> {
                const kind = String(row?.output_content_kind || '').toLowerCase();
                const isException = kind === 'exception';
                return (
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: 4,
                    border: `1px solid ${isException ? '#ffa940' : '#73d13d'}`,
                    background: isException ? '#fff7e6' : '#f6ffed',
                    color: isException ? '#fa8c16' : '#389e0d',
                    lineHeight: 1.5,
                  }}>
                    {String(v || '-')}
                  </span>
                );
              }},
              { title: '容器内容统计', dataIndex: 'output_contents_summary', width: 160, render: (_:any, row:any) => {
                const kind = String(row?.output_content_kind || '').toLowerCase();
                const qty = Number(row?.output_contents_total_qty || 0);
                if (kind === 'exception') return `${Number.isFinite(qty) ? qty : 0}件`;
                const raw = String(row?.output_contents_summary || '').trim();
                if (raw && raw.includes('款') && raw.includes('件')) return raw;
                const m = String(row?.output_contents_detail || '').split(/[；;]/).map((x:any)=> String(x || '').trim()).filter(Boolean).length;
                return `${Math.max(0, m)}款${Math.max(0, qty)}件`;
              }},
              { title: '容器内容', dataIndex: 'output_contents_detail', width: 260, render: (v:any, row:any) => {
                const kind = String(row?.output_content_kind || '').toLowerCase();
                if (kind === 'exception') {
                  return (
                    <Tooltip title="无法操作等异常产品">
                      <div style={{ color: '#595959', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        无法操作等异常产品
                      </div>
                    </Tooltip>
                  );
                }
                const full = String(v || '').trim();
                if (!full) return '-';
                const lines = full.split(/[；;]/).map((x) => String(x || '').trim()).filter(Boolean);
                if (lines.length === 0) return '-';
                const preview = lines.slice(0, 3);
                const hasMore = lines.length > 3;
                return (
                  <div style={{ maxWidth: 300 }}>
                    {preview.map((line, idx) => (
                      <div
                        key={`${line}-${idx}`}
                        style={{
                          position: 'relative',
                          minHeight: 20,
                          marginBottom: idx === preview.length - 1 ? 0 : 2,
                        }}
                      >
                        <Tooltip title={line}>
                          <div
                            style={{
                              color: '#595959',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              paddingRight: hasMore && idx === preview.length - 1 ? 56 : 0,
                            }}
                          >
                            {line}
                          </div>
                        </Tooltip>
                        {hasMore && idx === preview.length - 1 ? (
                          <Button
                            type="link"
                            size="small"
                            style={{
                              position: 'absolute',
                              right: 0,
                              bottom: -2,
                              padding: 0,
                              height: 'auto',
                              lineHeight: 1.2,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              const iocCode = String(row?.output_code || row?.display_code || '-');
                              Modal.info({
                                title: (
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 12, paddingRight: 24 }}>
                                    <span style={{ justifySelf: 'start' }}>容器内容完整记录</span>
                                    <span style={{ justifySelf: 'center', color: '#1677ff', fontWeight: 700, fontFamily: 'monospace' }}>{iocCode}</span>
                                    <span />
                                  </div>
                                ),
                                width: 760,
                                centered: true,
                                content: (
                                  <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                                    {lines.map((x, i) => (
                                      <div key={`${x}-${i}`} style={{ marginBottom: 6, wordBreak: 'break-all' }}>
                                        {i + 1}. {x}
                                      </div>
                                    ))}
                                  </div>
                                ),
                              });
                            }}
                          >
                            [...]
                          </Button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                );
              }},
            ] as any}
          />
        </Modal>

        {/* 预估操作数量明细弹窗（当前可用库存，对齐列表页） */}
        <Modal
          title={(
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%', paddingRight: 140 }}>
              <span>操作对象明细(当前可用库存)</span>
              {targetsOf?.system_code && (
                <span style={{ marginLeft: 12, marginRight: 'auto', color: '#d9480f', fontSize: 'inherit' }}>
                  {String(targetsOf.system_code)}
                </span>
              )}
              <span>
                <span style={{ display:'inline-block', padding:'1px 6px', border:'1px solid #91caff', background:'#e6f4ff', color:'#0958d9', borderRadius:4, fontWeight:600, fontSize:12, marginRight:6 }}>
                  涉及项目数量 {iuProjectCount}
                </span>
                <span style={{ display:'inline-block', padding:'1px 6px', border:'1px solid #ffe58f', background:'#fffbe6', color:'#ad8b00', borderRadius:4, fontWeight:600, fontSize:12, marginRight:6 }}>
                  操作对象种类 {iuTotalKinds} | 操作全部库存种类 {iuAllKinds} | 指定操作数量种类 {iuPartKinds}
                </span>
                <span style={{ display:'inline-block', padding:'1px 6px', border:'1px solid #b7eb8f', background:'#f6ffed', color:'#389e0d', borderRadius:4, fontWeight:600, fontSize:12 }}>
                  预计操作总数量 {iuEstimatedTotal}
                </span>
              </span>
            </div>
          )}
          open={!!targetsOf}
          onCancel={() => setTargetsOf(null)}
          footer={null}
          width={1200}
        >
          <Table
            size="small"
            rowKey={(r:any, index?: number) => String(r?.id ?? index)}
            dataSource={iuTargetsDataSource}
            pagination={{
              current: iuTargetsPage,
              pageSize: iuTargetsPageSize,
              total: Array.isArray(iuTargetsRows) ? iuTargetsRows.length : 0,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['10','20','50','100'] as any,
              onChange: (p:number, ps:number) => { setIuTargetsPage(p); setIuTargetsPageSize(ps); },
            }}
            onRow={(r:any) => {
              const isAll = !!r?.use_all_available;
              return {
                style: {
                  backgroundColor: isAll ? '#f6ffed' : '#fff0f6',
                },
              };
            }}
            columns={[
              {
                title: '项目',
                key: 'project',
                render: (_:any, row:any) => iuRenderProjectReadable(row),
              },
              {
                title: '类型',
                dataIndex: 'target_type',
                key: 'type',
                render: (_v:any, row:any) => iuRenderTargetTypeReadable(row),
              },
              {
                title: '库存单元系统编码',
                key: 'inventory_unit',
                render: (_:any, row:any) => iuRenderInventoryUnitReadable(row),
              },
              {
                title: '分拣组',
                key: 'sorting_group',
                render: (_:any, row:any) => iuRenderSortingGroupReadable(row),
              },
              { title: '库存单元内容项', key: 'inventory_unit_items', render: (_:any, row:any) => iuRenderUnitContentItems(row) },
              { title: '上游指令ID', dataIndex: 'prev_instruction_id', key: 'prev_instruction_id', render: (v:any) => v ?? '-' },
              { title: '是否操作全部库存', dataIndex: 'use_all_available', key: 'use_all_available', render: (v:any) => {
                const isAll = !!v;
                const text = isAll ? '是' : '否';
                const style = isAll
                  ? { backgroundColor: '#f6ffed', color: '#389e0d', border: '1px solid #b7eb8f', borderRadius: 4, padding: '1px 8px', fontWeight: 600, display: 'inline-block' }
                  : { backgroundColor: '#fff0f6', color: '#c41d7f', border: '1px solid #ffadd2', borderRadius: 4, padding: '1px 8px', fontWeight: 600, display: 'inline-block' };
                return <span style={style}>{text}</span>;
              } },
              { title: '预计数量', dataIndex: 'expected_qty', key: 'expected_qty', render: (v:any) => (v ?? 0) },
            ] as any}
          />
        </Modal>
      </Card>
    </div>
  );
};

export default StaffExecutionTodoPage;


